// Task Replay tab: re-run real tasks (mined from recorded sessions) across
// (agent × model) variants and score each against what the human shipped. Three
// levels, all shareable/reload-survivable URLs (same idiom as Benchmarks):
//
//   L0  overview  #/taskreplay                          routing view: recommendations,
//                                                        cost/quality scatter, route-by-type
//                                                        heatmap, leaderboard, runs list
//   L1  run page  #/taskreplay?run=<id>                  one sweep: KPI strip, two trade-off
//                                                        scatters, type×variant heatmap, and
//                                                        the task×variant matrix
//   L2  cell      #/taskreplay?run=<id>&tr=<t>|<a>|<m>   one cell: judge rail (score
//                                                        composition + criteria radar) +
//                                                        tabbed transcript/diff/reference
//
// A "variant" is one (agent, model) pair — a run can carry the same model under
// two agents, so the cell key is "<task>|<agent>|<model>". Data comes from the DB
// via /api/task-replay (L0), /run (L1), /result (L2). A running run fills its grid
// live: L0/L1 poll while any run is in flight.
import { state, $, esc, get, postStatus, renderMd, usd, num } from './core'
import { syncHash } from './router'
import { MAX_CELLS, encodeFilterList, filterList } from '../shared/task-replay'
import { renderTranscript, wireTranscript } from './transcript'

// Task types we replay, in display order, with the friendly label + blurb.
const TYPES: { key: string; label: string; blurb: string }[] = [
  { key: 'coding:explore', label: 'Explore', blurb: 'Investigate/answer a codebase question; scored vs the original analysis (3-pt consistency).' },
  { key: 'coding:plan', label: 'Plan', blurb: 'Design/plan a change; scored vs the original plan on a weighted rubric (candidate may exceed it).' },
  { key: 'coding:implement', label: 'Implement', blurb: 'Build the change; produced diff scored against the ground-truth PR diff.' },
  { key: 'coding:review', label: 'Review', blurb: 'Review the code/changes; scored vs the original review for consistency of key issues + verdict.' },
  { key: 'coding:testing', label: 'Testing', blurb: 'Add/adjust tests; produced diff scored against the ground-truth PR diff.' },
  { key: 'writing:email', label: 'Email', blurb: 'Draft the email/message; scored vs the original for core message + key points.' },
  { key: 'writing:doc', label: 'Doc', blurb: 'Write/update the document; scored vs the original for substance + key points.' },
  { key: 'writing:summary', label: 'Summary', blurb: 'Summarize; scored vs the original summary for substance.' },
]

// Per-variant categorical colours (scatter points + legend). Fixed order, never
// cycled — the 13th variant folds to grey rather than reusing a hue.
const TR_COLORS = ['#0f7a55', '#b8860b', '#b4452f', '#2f6f9f', '#7a4fa3', '#1c8a8a', '#a3562f', '#5c7a1c', '#8a6d3b', '#356859', '#9f5b8f', '#6b6b2f']

// Task-type categorical colours (fixed order, matches TYPES) for the suite
// composition bars + their legend.
const TYPE_COLORS: Record<string, string> = {
  'coding:explore': '#0f7a55', 'coding:plan': '#3b6ea5', 'coding:implement': '#bf6a2e',
  'coding:review': '#8a5fb0', 'coding:testing': '#3f9a8c', 'writing:email': '#c99a3f',
  'writing:doc': '#a24f63', 'writing:summary': '#6f7b3d',
}
const typeColor = (k: string): string => TYPE_COLORS[k] || '#8a857a'

let ovCache: any = null // L1 payload {runs, leaderboard, byType, filters} for the current suite
let suitesCache: any = null // L0 payload {suites}
const runCache = new Map<string, any>() // L2 payload by runId
let pollTimer: any = null

// L0 leaderboard controls (client-side over ovCache; no refetch).
let lbSort = 'mean'
let lbDir = -1
let lbQuery = ''
// L1 matrix controls (client-side over the cached grid).
let mxQuery = ''
let mxAttn = false
let mxSort: 'type' | 'disagree' | 'mean' = 'type'
// L1 scatter controls: which task types to aggregate over ([] = all), and which
// quality metric on Y.
let scTypes: string[] = []
let scMetric: 'score' | 'resolve' = 'score'
// Pareto overlay toggle — off by default; drives every scatter (L0 + L1).
let scPareto = false
// L2 open evidence tab.
let trTab = 'analysis'

// ---- router integration -----------------------------------------------------

export function getTrParams(): Record<string, string> {
  const q: Record<string, string> = {}
  if (state.tr.suite) q.suite = state.tr.suite
  if (state.tr.run) q.run = state.tr.run
  if (state.tr.sel) q.tr = state.tr.sel
  if (state.tr.ltype.length) q.type = encodeFilterList(state.tr.ltype)
  if (state.tr.lrepo.length) q.repo = encodeFilterList(state.tr.lrepo)
  if (state.tr.lagent.length) q.agent = encodeFilterList(state.tr.lagent)
  return q
}

export function applyTrParams(query: Record<string, string>): void {
  state.tr = {
    suite: query.suite || '', run: query.run || '', sel: query.tr || '',
    ltype: filterList(query.type), lrepo: filterList(query.repo), lagent: filterList(query.agent),
  }
  renderTaskReplay()
}

// L0 suites landing.
function goSuites(): void { state.tr = { suite: '', run: '', sel: '', ltype: [], lrepo: [], lagent: [] }; ovCache = null; suitesCache = null; syncHash(); renderTaskReplay() }
// L1 one suite: its leaderboard/charts/runs, scoped to the suite's runs.
function goSuite(suiteId: string): void { state.tr = { suite: suiteId, run: '', sel: '', ltype: [], lrepo: [], lagent: [] }; ovCache = null; syncHash(); renderTaskReplay() }
// L1 overview within the current suite (back from a run).
function goOverview(): void { state.tr = { suite: state.tr.suite, run: '', sel: '', ltype: state.tr.ltype, lrepo: state.tr.lrepo, lagent: state.tr.lagent }; syncHash(); renderTaskReplay() }
function goRun(runId: string): void { state.tr = { suite: state.tr.suite, run: runId, sel: '', ltype: [], lrepo: [], lagent: [] }; mxQuery = ''; mxAttn = false; mxSort = 'type'; scTypes = []; scMetric = 'score'; syncHash(); renderTaskReplay() }
function goCell(sel: string): void { state.tr = { ...state.tr, sel }; trTab = 'analysis'; syncHash(); renderTaskReplay() }

function parseSel(key: string): { task: string; agent: string; model: string } | null {
  const p = key.split('|')
  return p.length === 3 ? { task: p[0]!, agent: p[1]!, model: p[2]! } : null
}

// ---- entry -------------------------------------------------------------------

export function renderTaskReplay(): void {
  const root = $('#taskreplay')
  if (!root) return
  hideTip()
  if (pollTimer) { clearTimeout(pollTimer); pollTimer = null }
  if (state.tr.run && state.tr.sel) { paintDetail(root, state.tr.run, state.tr.sel); return }
  if (state.tr.run) { renderRun(root, state.tr.run, false); return }
  if (state.tr.suite) { renderOverview(root, false); return }
  renderSuites(root, false)
}

// ==== shared format + viz helpers ============================================

const pct = (x: number | null | undefined): string => (x == null ? '—' : Math.round(x * 100) + '%')

/** Wall-clock time, human-readable: 47s · 3.2m · 1.4h. */
const fmtDur = (s: number | null | undefined): string => {
  if (s == null || !isFinite(s)) return '—'
  if (s < 90) return Math.round(s) + 's'
  if (s < 3600) return (s / 60).toFixed(1) + 'm'
  return (s / 3600).toFixed(1) + 'h'
}
/** Compact token count: 1.6M · 24k · 812. */
const fmtTok = (n: number | null | undefined): string => {
  if (n == null) return '—'
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M'
  if (n >= 1e3) return Math.round(n / 1e3) + 'k'
  return num(n)
}

/** Map a 0..1 score to a colorblind-safe background (red → amber → green). */
export function scoreColor(x: number | null | undefined): string {
  if (x == null) return 'transparent'
  const hue = 8 + Math.max(0, Math.min(1, x)) * 132
  return 'hsl(' + Math.round(hue) + ', 55%, 82%)'
}

/** Trim a provider-prefixed model id to a compact label. */
function shortModel(model: string): string {
  let m = String(model || '')
  const slash = m.lastIndexOf('/')
  if (slash >= 0) m = m.slice(slash + 1)
  // Bedrock ids carry a region + vendor prefix and a version suffix
  // (us.anthropic.claude-haiku-4-5-20251001-v1:0) that make every Claude column read
  // "us.anthropi…"; keep the model name only, named like the gateway's (opus-5).
  return m.replace(/^opencode-go-/, '').replace(/^bedrock-claude-/, '')
    .replace(/^(?:us|eu|apac|au|jp|global)\.(?=[a-z-]+\.)/, '').replace(/^anthropic\.claude-/, '')
    .replace(/-\d{8}(?:-v\d+(?::\d+)?)?$/, '').replace(/-v\d+:\d+$/, '')
}
function providerOf(model: string): string { return String(model || '').includes('/') ? String(model).split('/')[0]! : 'direct' }

/** A 0..1 score as a labelled bar (mirrors Benchmarks' scoreBar). */
export function scoreBar(x: number | null | undefined): string {
  if (x == null) return '<span class="tr-na">—</span>'
  const n = Math.round(x * 100)
  return '<span class="bench-rate"><span class="bench-rate-num">' + n + '%</span>' +
    '<span class="bar-track"><span class="bar-fill" style="width:' + n + '%"></span></span></span>'
}

/** Score-over-time as a tiny inline SVG polyline; a single point renders as a dot. */
function sparkline(vals: number[]): string {
  if (!vals || !vals.length) return '<span class="tr-spark-empty">—</span>'
  const W = 64, H = 18, pad = 2
  if (vals.length === 1) {
    const cy = H - pad - vals[0]! * (H - 2 * pad)
    return '<svg class="tr-spark-svg" viewBox="0 0 ' + W + ' ' + H + '"><circle cx="' + (W / 2) + '" cy="' + cy.toFixed(1) + '" r="2.2" class="tr-spark-dot"/></svg>'
  }
  const step = (W - 2 * pad) / (vals.length - 1)
  const pts = vals.map((v, i) => (pad + i * step).toFixed(1) + ',' + (H - pad - v * (H - 2 * pad)).toFixed(1)).join(' ')
  return '<svg class="tr-spark-svg" viewBox="0 0 ' + W + ' ' + H + '"><polyline points="' + pts + '" class="tr-spark-line"/></svg>'
}

function verdictClass(v: string | null | undefined): string {
  return v === 'resolved' ? 'b-success' : v === 'unresolved' ? 'b-failure' : v === 'unscored' ? 'b-null' : 'b-partial'
}
function verdictBadge(v: string | null | undefined): string {
  if (!v) return '<span class="badge b-null">—</span>'
  return '<span class="badge ' + verdictClass(v) + '">' + esc(String(v)) + '</span>'
}
export function statusPill(s: string, label = s): string {
  const cls = s === 'done' ? 'b-success' : s === 'running' ? 'b-partial' : s === 'failed' ? 'b-failure' : s === 'cancelled' ? 'b-null' : 'b-unknown'
  const spin = s === 'running' ? '<span class="bench-spin"></span>' : ''
  return '<span class="badge ' + cls + ' tr-statuspill">' + spin + esc(label) + '</span>'
}
function progressBar(done: number, total: number): string {
  const p = total ? Math.min(100, Math.round((done / total) * 100)) : 0
  return '<span class="bench-prog"><span class="bar-track"><span class="bar-fill" style="width:' + p + '%"></span></span>' +
    '<span class="bench-prog-n">' + num(done) + ' / ' + num(total) + '</span></span>'
}
function typeLabel(key: string | null | undefined): string {
  const ty = TYPES.filter((x) => x.key === key)[0]
  return ty ? ty.label : (key || '')
}
/** Distinct task types in canonical TYPES order; unknown types after, A→Z. */
function sortTypes(types: string[]): string[] {
  const rank = (t: string) => { const i = TYPES.findIndex((x) => x.key === t); return i < 0 ? TYPES.length : i }
  return [...new Set(types)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

// ---- multi-select filter dropdown ---------------------------------------------
// A button + checkbox popover. The selection is a value list where [] means "all"
// (no filter); ticking every option collapses back to [] so the URL/API never
// carry a redundant full list. A selected value missing from the options (a stale
// link, or data that has since gone) stays listed, flagged, so the filter is never
// silently applied yet invisible — unless `dropUnknown` (client-side facets, where
// such a value just matches nothing and is discarded).

export type MsOpt = { v: string; label: string }

/** Normalize a selection against its options: known-only when `dropUnknown`, and
 *  every-option-ticked ⇒ [] (all). */
function msNormalize(sel: string[], opts: MsOpt[], dropUnknown = false): string[] {
  const known = new Set(opts.map((o) => o.v))
  const out = dropUnknown ? sel.filter((v) => known.has(v)) : sel.slice()
  return opts.length && out.length === opts.length && out.every((v) => known.has(v)) ? [] : out
}

function msSummary(sel: string[], opts: MsOpt[], allLabel: string, noun: string): string {
  if (!sel.length) return allLabel
  const lab = (v: string) => (opts.find((o) => o.v === v) || { label: v }).label
  if (sel.length === 1) return lab(sel[0]!)
  return sel.length === 2 ? lab(sel[0]!) + ', ' + lab(sel[1]!) : sel.length + ' ' + noun
}

export function msSelect(id: string, label: string, allLabel: string, noun: string, opts: MsOpt[], selected: string[],
  o: { dropUnknown?: boolean } = {}): string {
  const sel = msNormalize(selected, opts, o.dropUnknown)
  const unknown = sel.filter((v) => !opts.some((x) => x.v === v)).map((v) => ({ v, label: v, stale: true }))
  const all = [...opts.map((x) => ({ ...x, stale: false })), ...unknown]
  const row = (x: { v: string; label: string; stale: boolean }) =>
    '<label class="tr-ms-opt' + (x.stale ? ' stale' : '') + '"' + (x.stale ? ' title="No longer in this data — untick to clear"' : '') + '>' +
    '<input type="checkbox" value="' + esc(x.v) + '"' + (sel.indexOf(x.v) >= 0 ? ' checked' : '') + '>' +
    '<span class="tr-ms-lab">' + esc(x.label) + '</span>' +
    '<button type="button" class="tr-ms-only" data-only="' + esc(x.v) + '" tabindex="-1">only</button></label>'
  return '<div class="tr-ms" id="' + id + '" data-all="' + esc(allLabel) + '" data-noun="' + esc(noun) + '"' + (o.dropUnknown ? ' data-drop="1"' : '') + '>' +
    '<span class="tr-ms-flab">' + esc(label) + '</span>' +
    '<button type="button" class="tr-filter tr-ms-btn' + (sel.length ? ' on' : '') + '" aria-haspopup="true" aria-expanded="false"' +
    (all.length ? '' : ' disabled') + '>' +
    '<span class="tr-ms-val">' + esc(msSummary(sel, opts, allLabel, noun)) + '</span></button>' +
    '<div class="tr-ms-pop" hidden>' +
    '<label class="tr-ms-opt tr-ms-all"><input type="checkbox" data-all' + (sel.length ? '' : ' checked') + '><span class="tr-ms-lab">' + esc(allLabel) + '</span></label>' +
    '<div class="tr-ms-list">' + all.map(row).join('') + '</div></div></div>'
}

// Open dropdown's close handler — at most one is open at a time.
let msOpenClose: (() => void) | null = null
let msDocWired = false

/** Wire a msSelect. `onChange(values)` fires on close when the selection changed
 *  (server-side filters — one refetch per pick session), or on every tick with
 *  `live` (client-side re-plots). Outside click, Escape and re-clicking the button
 *  all close it. */
export function wireMs(root: HTMLElement, id: string, onChange: (vals: string[]) => void, o: { live?: boolean } = {}): void {
  const el = root.querySelector('#' + id) as HTMLElement | null
  if (!el) return
  const btn = el.querySelector('.tr-ms-btn') as HTMLButtonElement
  const pop = el.querySelector('.tr-ms-pop') as HTMLElement
  const allBox = el.querySelector('input[data-all]') as HTMLInputElement
  const boxes = Array.prototype.slice.call(el.querySelectorAll('.tr-ms-list input')) as HTMLInputElement[]
  const opts: MsOpt[] = Array.prototype.map.call(el.querySelectorAll('.tr-ms-opt:not(.stale):not(.tr-ms-all) input'),
    (b: HTMLInputElement) => ({ v: b.value, label: (b.nextElementSibling as HTMLElement).textContent || b.value })) as MsOpt[]
  const drop = el.hasAttribute('data-drop')
  const current = () => msNormalize(boxes.filter((b) => b.checked).map((b) => b.value), opts, drop)
  let initial = current().join('\n')

  // Reflect a selection in the button label (+ the boxes, for All/only — a user's
  // own ticks are left as ticked, even when they add up to all); fire live listeners.
  const sync = (vals: string[], setBoxes: boolean) => {
    if (setBoxes) boxes.forEach((b) => { b.checked = vals.indexOf(b.value) >= 0 })
    allBox.checked = !vals.length
    btn.classList.toggle('on', !!vals.length)
    ;(btn.querySelector('.tr-ms-val') as HTMLElement).textContent =
      msSummary(vals, opts, el.getAttribute('data-all') || '', el.getAttribute('data-noun') || '')
    if (o.live) { initial = vals.join('\n'); onChange(vals) }
  }
  const close = () => {
    if (msOpenClose === close) msOpenClose = null
    // Detached by a repaint/navigation: nothing to apply to any more.
    if (pop.hidden || !el.isConnected) return
    pop.hidden = true; el.classList.remove('open'); btn.setAttribute('aria-expanded', 'false')
    const vals = current()
    if (!o.live && vals.join('\n') !== initial) { initial = vals.join('\n'); onChange(vals) }
  }
  btn.onclick = (ev) => {
    ev.stopPropagation()
    if (!pop.hidden) { close(); return }
    if (msOpenClose) msOpenClose()
    pop.hidden = false; el.classList.add('open'); btn.setAttribute('aria-expanded', 'true')
    msOpenClose = close
    ;(boxes[0] || allBox).focus()
  }
  pop.onclick = (ev) => ev.stopPropagation()
  // "All" = clear the filter. Ticking it can't be undone by unticking (there is
  // no "none" state), so an untick just re-ticks.
  allBox.onchange = () => sync([], true)
  // Unticking the last box — or ticking every one — lands on [] = all.
  boxes.forEach((b) => { b.onchange = () => sync(current(), false) })
  Array.prototype.forEach.call(el.querySelectorAll('.tr-ms-only'), (b: HTMLElement) => {
    b.onclick = (ev) => { ev.preventDefault(); ev.stopPropagation(); sync(msNormalize([b.getAttribute('data-only') || ''], opts, drop), true) }
  })
  el.onkeydown = (ev) => { if (ev.key === 'Escape' && !pop.hidden) { ev.stopPropagation(); close(); btn.focus() } }
  if (!msDocWired) {
    // Outside click closes (and so applies) whichever dropdown is open.
    document.addEventListener('click', () => { if (msOpenClose) msOpenClose() })
    msDocWired = true
  }
}
function when(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (!then) return String(iso).slice(0, 10)
  const mins = Math.round((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return mins + 'm ago'
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return hrs + 'h ago'
  return Math.round(hrs / 24) + 'd ago'
}
function isLive(r: any): boolean { return r && (r.status === 'running' || r.status === 'queued') }
/** Absolute short date ("Sep 15, 2026") — for freeze stamps; tolerant of the
 *  mixed ISO/space timestamp formats in imported rows. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return String(iso).slice(0, 10)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
function schedulePoll(root: HTMLElement): void {
  if (pollTimer) clearTimeout(pollTimer)
  pollTimer = setTimeout(() => {
    if (state.view !== 'taskreplay') return
    // A repaint would destroy an open filter dropdown mid-pick — wait it out.
    if (document.querySelector('.tr-ms.open')) { schedulePoll(root); return }
    if (state.tr.run && !state.tr.sel) renderRun(root, state.tr.run, true)
    else if (state.tr.suite) renderOverview(root, true)
    else renderSuites(root, true)
  }, 4000)
}

// ---- shared tooltip ----------------------------------------------------------
// One fixed element reused by every hoverable mark ([data-tip]); the tip text is
// "<title>\n<detail>" — first line bold, rest muted.

function ensureTip(): HTMLElement {
  let t = document.getElementById('trTip')
  if (!t) { t = document.createElement('div'); t.id = 'trTip'; t.className = 'tr-tip'; document.body.appendChild(t) }
  return t
}
// A hovered mark that navigates (e.g. a matrix cell → cell detail) never fires its
// own mouseleave — the click re-renders the DOM out from under it — so the tip
// would hang around. Hide it explicitly whenever the view repaints.
function hideTip(): void { const t = document.getElementById('trTip'); if (t) t.style.display = 'none' }
function wireTips(root: HTMLElement): void {
  const tip = ensureTip()
  Array.prototype.forEach.call(root.querySelectorAll('[data-tip]'), (el: HTMLElement) => {
    el.onmouseenter = () => {
      const [head, ...rest] = (el.getAttribute('data-tip') || '').split('\n')
      tip.innerHTML = '<span class="tr-tip-h">' + esc(head || '') + '</span>' + (rest.length ? '<span class="tr-tip-b">' + esc(rest.join(' · ')) + '</span>' : '')
      tip.style.display = 'block'
    }
    el.onmousemove = (e: MouseEvent) => { tip.style.left = Math.min(e.clientX + 14, window.innerWidth - 240) + 'px'; tip.style.top = (e.clientY + 14) + 'px' }
    el.onmouseleave = () => { tip.style.display = 'none' }
  })
}

// ---- generic cost/quality scatter -------------------------------------------
// Each point is a variant. Up-and-left is better (high score, low x). Draws the
// Pareto frontier (no variant both cheaper AND better) + the best-value ★ (max
// score per unit x). Points carry data-key so a caller can wire click-to-filter.

interface ScatterPt { key: string; label: string; x: number; y: number; size: number }
function scatterSvg(points: ScatterPt[], xFmt: (v: number) => string, xLabel: string, opts?: { W?: number; H?: number; yLabel?: string; pareto?: boolean }): string {
  if (!points.length) return '<div class="empty">No scored variants yet.</div>'
  const pts = points.map((p, i) => ({ ...p, color: TR_COLORS[i % TR_COLORS.length]! }))
  const W = opts?.W ?? 520, H = opts?.H ?? 360, padL = 48, padR = 16, padT = 14, padB = 46
  const yLabel = opts?.yLabel ?? 'Mean score'
  const pareto = !!opts?.pareto
  const pw = W - padL - padR, ph = H - padT - padB
  const xmax = Math.max.apply(null, pts.map((p) => p.x)) * 1.1 || 1
  let ymin = Math.min.apply(null, pts.map((p) => p.y)), ymax = Math.max.apply(null, pts.map((p) => p.y))
  const m = (ymax - ymin) * 0.14 || 0.05; ymin = Math.max(0, ymin - m); ymax = Math.min(1, ymax + m)
  const sx = (v: number) => padL + (v / xmax) * pw
  const sy = (v: number) => padT + (1 - (ymax === ymin ? 0.5 : (v - ymin) / (ymax - ymin))) * ph
  const maxSize = Math.max.apply(null, pts.map((p) => p.size || 0)) || 1
  const rad = (n: number) => 6 + 7 * Math.sqrt((n || 0) / maxSize)
  const front = pts.filter((p) => !pts.some((q) => q !== p && q.x <= p.x && q.y >= p.y && (q.x < p.x || q.y > p.y)))
  const frontSet = new Set(front)
  let best: typeof pts[0] | null = null
  if (pareto) pts.forEach((p) => { if (p.x > 0 && p.y > 0 && (!best || p.y / p.x > best.y / best.x)) best = p })

  let g = '<svg class="bench-scatter-svg" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="xMidYMid meet">'
  for (let i = 0; i <= 4; i++) {
    const yv = ymin + (ymax - ymin) * (i / 4), py = sy(yv)
    g += '<line class="bench-scatter-grid" x1="' + padL + '" y1="' + py + '" x2="' + (padL + pw) + '" y2="' + py + '"/>'
    g += '<text class="bench-scatter-tick" x="' + (padL - 8) + '" y="' + (py + 4) + '" text-anchor="end">' + pct(yv) + '</text>'
    const xv = xmax * (i / 4), px = sx(xv)
    g += '<line class="bench-scatter-grid" x1="' + px + '" y1="' + padT + '" x2="' + px + '" y2="' + (padT + ph) + '"/>'
    g += '<text class="bench-scatter-tick" x="' + px + '" y="' + (padT + ph + 18) + '" text-anchor="middle">' + esc(xFmt(xv)) + '</text>'
  }
  g += '<text class="bench-scatter-axis" x="' + (padL + pw / 2) + '" y="' + (H - 6) + '" text-anchor="middle">' + esc(xLabel) + ' →</text>'
  g += '<text class="bench-scatter-axis" x="14" y="' + (padT + ph / 2) + '" text-anchor="middle" transform="rotate(-90 14 ' + (padT + ph / 2) + ')">' + esc(yLabel) + ' →</text>'
  if (pareto) {
    const fs = front.slice().sort((a, b) => a.x - b.x)
    if (fs.length > 1) g += '<polyline points="' + fs.map((p) => sx(p.x).toFixed(1) + ',' + sy(p.y).toFixed(1)).join(' ') + '" fill="none" stroke="#9a8c78" stroke-width="1.5" stroke-dasharray="2 4" stroke-linecap="round" stroke-opacity="0.75"/>'
  }
  pts.forEach((p) => {
    const cx = sx(p.x), cy = sy(p.y), r = rad(p.size), onFront = frontSet.has(p)
    if (pareto && onFront) g += '<circle cx="' + cx + '" cy="' + cy + '" r="' + (r + 4) + '" fill="none" stroke="' + p.color + '" stroke-opacity="0.6" stroke-width="2"/>'
    g += '<circle class="tr-sc-dot' + (pareto && !onFront ? ' tr-sc-dim' : '') + '" data-key="' + esc(p.key) + '" data-tip="' + esc(p.label + '\n' + pct(p.y) + ' • ' + xFmt(p.x) + ' • ' + p.size + ' cells') + '" cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + p.color + '"/>'
    if (pareto && onFront) g += '<text class="tr-sc-num" x="' + cx + '" y="' + (cy - r - 6) + '" text-anchor="middle">' + pct(p.y) + '</text>'
    if (pareto && p === best) g += '<text class="bench-scatter-best" x="' + cx + '" y="' + (cy - r - (onFront ? 19 : 6)) + '" text-anchor="middle">★</text>'
  })
  g += '</svg>'
  const legend = '<div class="tr-scatter-vlegend">' + pts.map((p) =>
    '<span class="tr-scatter-vkey' + (pareto && frontSet.has(p) ? ' tr-sc-vkey-on' : '') + '" data-key="' + esc(p.key) + '"><span class="tr-scatter-vdot" style="background:' + p.color + '"></span>' + esc(p.label) + '</span>').join('') + '</div>'
  const note = '<div class="tr-sc-note">bubble = cells scored' + (pareto ? ' · ◯ on frontier · ★ best value (score per unit)' : '') + '</div>'
  return g + note + legend
}

// Pareto toggle + info affordance shared by the L0 and L1 scatters. One global
// `scPareto` flag drives every scatter; the handlers just re-render in place.
function paretoToggle(): string {
  return '<span class="tr-pareto-ctl">' +
    '<button type="button" class="tr-pareto-btn' + (scPareto ? ' on' : '') + '" id="trParetoBtn" role="switch" aria-checked="' + scPareto + '"><span class="tr-pareto-knob"></span></button>' +
    '<span class="tr-pareto-lab">Show Pareto</span>' +
    '<span class="tr-info" tabindex="0" data-tip="' + esc('Pareto frontier\nThe best score you can get at each price. Models on the line are the best deals; anything below is overpriced for the score it earns.') + '">i</span></span>'
}

// ---- generic score heatmap ---------------------------------------------------
// rows × cols of 0..1 means. Best cell per row is boxed. getVal returns null for
// an absent cell (rendered as a muted dot).

export interface HmCol { key: string; label: string; sub?: string }
export interface HmRow { key: string; label: string; emph?: boolean }
export function heatmap(rows: HmRow[], cols: HmCol[], getVal: (rowKey: string, colKey: string) => number | null, corner = '',
  opts: { tieWins?: boolean } = {}): string {
  if (!rows.length || !cols.length) return '<div class="empty">—</div>'
  const head = '<tr><th class="tr-hm-rt tr-hm-corner">' + esc(corner) + '</th>' + cols.map((c) =>
    '<th class="tr-hm-col" data-tip="' + esc(c.label + (c.sub ? '\n' + c.sub : '')) + '"><span class="tr-hm-cm">' + esc(c.label) + '</span>' + (c.sub ? '<span class="tr-hm-cs">' + esc(c.sub) + '</span>' : '') + '</th>').join('') + '</tr>'
  const body = rows.map((r) => {
    const vals = cols.map((c) => getVal(r.key, c.key))
    const mx0 = Math.max.apply(null, vals.filter((v): v is number => v != null).concat([-1]))
    const mx = opts.tieWins === false && vals.filter((v) => v === mx0).length > 1 ? Infinity : mx0
    return '<tr class="' + (r.emph ? 'tr-hm-allrow' : '') + '"><th class="tr-hm-rt" data-r="' + esc(r.key) + '">' + esc(r.label) + '</th>' +
      vals.map((v, i) => v == null
        ? '<td class="tr-hm-na">·</td>'
        : '<td class="tr-hm-cell' + (v === mx ? ' tr-hm-win' : '') + '" data-r="' + esc(r.key) + '" data-c="' + esc(cols[i]!.key) + '" style="background:' + scoreColor(v) + '" ' +
          'data-tip="' + esc(r.label + ' · ' + cols[i]!.label + '\n' + pct(v) + ' mean') + '">' + Math.round(v * 100) + '</td>').join('') + '</tr>'
  }).join('')
  return '<div class="tr-hm-scroll"><table class="tr-hm2">' + head + body + '</table></div>'
}

// ==== L0 suites landing (Tier 0) =============================================

function renderSuites(root: HTMLElement, background: boolean): void {
  if (!background && !suitesCache) root.innerHTML = '<div class="empty">Loading suites…</div>'
  get('/api/task-replay/suites?_=' + Date.now())
    // Paint even while another tab is showing: the page-load pre-render lands here
    // before the user tabs in, and tabbing in doesn't refetch.
    .then((data: any) => { suitesCache = data; if (!state.tr.suite && !state.tr.run) paintSuites(root) })
    .catch(() => { if (!background) root.innerHTML = '<div class="empty">Could not load suites — is the server reachable?</div>' })
}

const money = (n: number | null | undefined): string => (n == null ? '—' : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

/** Thin stacked task-type composition bar for a suite. */
function mixBar(dist: { taskType: string; n: number }[]): string {
  const total = dist.reduce((a, d) => a + d.n, 0) || 1
  // Render in fixed TYPES order for stable colour runs.
  const ordered = TYPES.map((t) => dist.find((d) => d.taskType === t.key)).filter(Boolean) as { taskType: string; n: number }[]
  const segs = ordered.map((d) => '<span data-tip="' + esc(typeLabel(d.taskType)) + '\n' + d.n + ' tasks" style="width:' + (d.n / total * 100).toFixed(2) + '%;background:' + typeColor(d.taskType) + '"></span>').join('')
  return '<div class="tr-mix">' + segs + '</div>'
}

function typeLegend(): string {
  return '<div class="tr-mix-legend">' + TYPES.map((t) =>
    '<span class="tr-mix-lk"><span class="tr-mix-sw" style="background:' + typeColor(t.key) + '"></span>' + esc(t.label) + '</span>').join('') + '</div>'
}

function paintSuites(root: HTMLElement): void {
  const suites: any[] = (suitesCache || {}).suites || []
  const totalTasks = suites.reduce((a, s) => a + (s.tasks || 0), 0)
  const totalRuns = suites.reduce((a, s) => a + (s.runs || 0), 0)
  const totalSpent = suites.reduce((a, s) => a + (s.spent || 0), 0)
  const head =
    '<div class="tr-l0-head"><div class="tr-l0-title"><h2>Task Replay</h2>' +
    '<div class="tr-lede">A <b>suite</b> is a frozen set of real mined tasks. Run models against a suite — every leaderboard, chart and recommendation is scoped to that one suite, so runs are truly comparable. Curate once; re-run as new models ship.</div></div>' +
    '<button type="button" class="btn tr-newsuite" id="trNewSuite">+ New suite</button></div>'
  if (!suites.length) {
    root.innerHTML = '<section class="tr-l0">' + head +
      '<div class="empty">No suites yet.<br>Create one with <b>+ New suite</b> — pick a set of tasks to freeze, then run models against it.</div></section>'
    wireSuites(root)
    return
  }
  const poolbar = '<div class="tr-poolbar">' +
    '<span><span class="mono">' + num(totalTasks) + '</span> tasks</span><span>·</span>' +
    '<span><span class="mono">' + suites.length + '</span> suites</span><span>·</span>' +
    '<span><span class="mono">' + num(totalRuns) + '</span> runs</span><span>·</span>' +
    '<span><span class="mono">' + money(totalSpent) + '</span> spent all-time</span></div>'
  const rows = suites.map((s) => {
    const repoLine = !s.repos.length ? '' : (s.repos.length <= 2 ? s.repos.join(', ') : s.repos.slice(0, 2).join(', ') + ' +' + (s.repos.length - 2))
    const pick = s.topQuality
      ? '<span class="tr-pick"><span class="tr-pick-m">' + esc(shortModel(s.topQuality.model)) + '</span><span class="tr-pick-v">' + pct(s.topQuality.mean) + '</span></span>'
      : '<span class="tr-muted">— no runs</span>'
    return '<tr class="tr-suiterow" data-suite="' + esc(s.id) + '">' +
      '<td><div class="tr-suite-name">' + esc(s.name) + '<span class="tr-suite-arrow">→</span></div>' +
      (repoLine ? '<div class="tr-suite-repos">' + esc(repoLine) + '</div>' : '') + '</td>' +
      '<td><div class="tr-suite-tasks"><span class="mono">' + num(s.tasks) + '</span><span class="tr-suite-u">tasks</span></div>' + mixBar(s.typeDist) + '</td>' +
      '<td class="r"><span class="mono">' + num(s.runs) + '</span><div class="tr-suite-sub">' + num(s.variants) + ' variants</div></td>' +
      '<td>' + pick + '</td>' +
      '<td class="r"><span class="mono">' + money(s.spent) + '</span></td>' +
      '<td class="tr-when">' + esc(fmtDate(s.frozenAt)) + '</td>' +
      '<td class="tr-suite-menu"><div class="feat-menu-wrap">' +
        '<button type="button" class="feat-menu-btn tr-suite-more" aria-label="Rename or delete">⋯</button>' +
        '<div class="feat-menu">' +
          '<button class="menu-item" data-act="rename" data-suite="' + esc(s.id) + '" data-name="' + esc(s.name) + '">Rename…</button>' +
          '<button class="menu-item danger" data-act="delete" data-suite="' + esc(s.id) + '" data-name="' + esc(s.name) + '" data-runs="' + s.runs + '" data-cells="' + s.cells + '">Delete…</button>' +
        '</div></div></td>' +
      '</tr>'
  }).join('')
  root.innerHTML = '<section class="tr-l0">' + head + poolbar + typeLegend() +
    '<div class="tr-card tr-card-flat"><table class="tr-suites"><thead><tr>' +
    '<th>Suite</th><th>Tasks</th><th class="r">Runs</th><th>Best quality</th><th class="r">Spent</th><th>Frozen</th><th></th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table></div></section>'
  wireSuites(root)
}

function wireSuites(root: HTMLElement): void {
  Array.prototype.forEach.call(root.querySelectorAll('.tr-suiterow'), (el: HTMLElement) => {
    el.onclick = (e) => {
      if ((e.target as HTMLElement).closest('.tr-suite-menu')) return // let the ⋯ menu handle it
      goSuite(el.getAttribute('data-suite') || '')
    }
  })
  const ns = root.querySelector('#trNewSuite') as HTMLElement | null
  if (ns) ns.onclick = () => openSuiteBuilder()
  // ⋯ dropdown (reuses the app's .feat-menu idiom: fixed-position, one open at a time).
  Array.prototype.forEach.call(root.querySelectorAll('.tr-suite-menu .feat-menu-btn'), (btn: HTMLElement) => {
    btn.onclick = (ev) => {
      ev.stopPropagation()
      const menu = btn.parentElement!.querySelector('.feat-menu') as HTMLElement
      const wasOpen = menu.classList.contains('on')
      closeSuiteMenus()
      if (wasOpen) return
      menu.classList.add('on')
      const rect = btn.getBoundingClientRect()
      menu.style.top = (rect.bottom + 4) + 'px'
      menu.style.left = (rect.right - menu.offsetWidth) + 'px'
    }
  })
  Array.prototype.forEach.call(root.querySelectorAll('.tr-suite-menu .feat-menu'), (m: HTMLElement) => { m.onclick = (ev) => ev.stopPropagation() })
  Array.prototype.forEach.call(root.querySelectorAll('.tr-suite-menu .menu-item'), (b: HTMLElement) => {
    b.onclick = () => {
      closeSuiteMenus()
      const id = b.getAttribute('data-suite') || '', name = b.getAttribute('data-name') || ''
      if (b.getAttribute('data-act') === 'delete') openSuiteDelete(root, id, name, Number(b.getAttribute('data-runs')) || 0, Number(b.getAttribute('data-cells')) || 0)
      else openSuiteRename(root, id, name)
    }
  })
  if (!suiteMenuDocWired) { document.addEventListener('click', closeSuiteMenus); suiteMenuDocWired = true }
  wireTips(root)
}

let suiteMenuDocWired = false
function closeSuiteMenus(): void {
  Array.prototype.forEach.call(document.querySelectorAll('#taskreplay .feat-menu.on'), (m: HTMLElement) => m.classList.remove('on'))
}

/** A small modal (reuses the .tr-modal chrome). `bodyHtml` fills the middle;
 *  wiring is left to the caller. Returns the backdrop element. */
function suiteModal(title: string, bodyHtml: string): HTMLElement {
  const prev = document.getElementById('tr-suite-modal'); if (prev) prev.remove()
  const el = document.createElement('div'); el.id = 'tr-suite-modal'; el.className = 'tr-modal-backdrop'
  el.innerHTML = '<div class="tr-modal tr-modal-sm">' +
    '<div class="tr-modal-head"><h2>' + esc(title) + '</h2><button type="button" class="tr-modal-x">✕</button></div>' +
    bodyHtml + '</div>'
  document.body.appendChild(el)
  const close = () => el.remove()
  ;(el.querySelector('.tr-modal-x') as HTMLElement).onclick = close
  el.onclick = (e) => { if (e.target === el) close() }
  return el
}

function openSuiteRename(root: HTMLElement, id: string, name: string): void {
  const el = suiteModal('Rename suite',
    '<label class="tr-fld"><span>Suite name</span><input id="trRenameInput" value="' + esc(name) + '"/></label>' +
    '<div class="tr-modal-foot"><span class="tr-estimate">The task set stays frozen — only the name changes.</span>' +
    '<button type="button" class="btn tr-cancel">Cancel</button><button type="button" class="btn tr-launch" id="trRenameSave">Save</button></div>')
  const close = () => el.remove()
  const input = el.querySelector('#trRenameInput') as HTMLInputElement
  const save = () => {
    const val = input.value.trim()
    if (!val || val === name) { close(); return }
    fetch('/api/task-replay/suites/' + encodeURIComponent(id), { method: 'PATCH', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name: val }) })
      .then(() => { close(); suitesCache = null; renderSuites(root, false) })
  }
  ;(el.querySelector('.tr-cancel') as HTMLElement).onclick = close
  ;(el.querySelector('#trRenameSave') as HTMLElement).onclick = save
  input.onkeydown = (e) => { if (e.key === 'Enter') save(); else if (e.key === 'Escape') close() }
  input.focus(); input.select()
}

function openSuiteDelete(root: HTMLElement, id: string, name: string, runs: number, cells: number): void {
  const impact = runs
    ? 'This also deletes its <b>' + runs + ' run' + (runs === 1 ? '' : 's') + '</b> (' + num(cells) + ' result cell' + (cells === 1 ? '' : 's') + ') — their scores and transcripts are removed.'
    : 'It has no runs.'
  const el = suiteModal('Delete suite',
    '<div class="tr-modal-msg">Delete <b>' + esc(name) + '</b>? ' + impact + ' This can’t be undone.</div>' +
    '<div class="tr-modal-foot"><span class="tr-estimate"></span>' +
    '<button type="button" class="btn tr-cancel">Cancel</button><button type="button" class="btn tr-danger" id="trDeleteGo">Delete suite</button></div>')
  const close = () => el.remove()
  ;(el.querySelector('.tr-cancel') as HTMLElement).onclick = close
  ;(el.querySelector('#trDeleteGo') as HTMLElement).onclick = () => {
    fetch('/api/task-replay/suites/' + encodeURIComponent(id), { method: 'DELETE', credentials: 'same-origin' })
      .then(() => { close(); suitesCache = null; renderSuites(root, false) })
  }
}

// ==== L1 overview (one suite) ================================================

// Bumped per overview fetch: only the latest response may paint, so a slow
// response for an older filter set (or a poll) can't overwrite a newer one.
let ovSeq = 0

function renderOverview(root: HTMLElement, background: boolean): void {
  const f = (k: string, v: string[]) => (v.length ? '&' + k + '=' + encodeURIComponent(encodeFilterList(v)) : '')
  const qs = '&suite=' + encodeURIComponent(state.tr.suite) +
    f('taskType', state.tr.ltype) + f('repo', state.tr.lrepo) + f('agent', state.tr.lagent)
  if (!background && !ovCache) root.innerHTML = '<div class="empty">Loading suite…</div>'
  const seq = ++ovSeq
  get('/api/task-replay?_=' + Date.now() + qs)
    .then((data: any) => {
      if (seq !== ovSeq) return
      ovCache = data
      if (state.view === 'taskreplay' && state.tr.suite && !state.tr.run) paintOverview(root)
    })
    .catch(() => { if (!background && seq === ovSeq) root.innerHTML = '<div class="empty">Could not load suite — is the server reachable?</div>' })
}

function paintOverview(root: HTMLElement): void {
  const d = ovCache || {}
  const runs: any[] = d.runs || []
  const lb: any[] = d.leaderboard || []
  const byType: any[] = d.byType || []
  const filters = d.filters || { taskTypes: [], repos: [] }
  const suite = d.suite || { name: 'Suite', tasks: 0 }
  const head = '<div class="panel-head tr-l0-head"><div class="tr-suite-hl">' + suitesBack() +
    '<div class="tr-l0-title"><h2>' + esc(suite.name) + '</h2>' +
    '<div class="tr-suite-sub"><span class="mono">' + num(suite.tasks || 0) + '</span> tasks · frozen suite · leaderboard scoped to this suite’s runs</div></div></div>' +
    '<button type="button" class="btn tr-newrun" id="trNewRun">+ New run</button></div>'
  if (!runs.length) {
    root.innerHTML = '<section class="tr-l0">' + head +
      '<div class="empty">No runs in this suite yet.<br>Launch one with <b>+ New run</b> — it will cover all ' +
      num(suite.tasks || 0) + ' tasks in the suite.</div></section>'
    wireOverview(root)
    return
  }
  root.innerHTML =
    '<section class="tr-l0">' + head +
    filterBar(filters) +
    recoBlock(lb) +
    '<div class="tr-block"><div class="tr-block-head"><h3>Quality vs. cost</h3><span class="tr-sub">up &amp; left is better</span>' + paretoToggle() + '</div>' +
    '<div class="tr-card"><div id="trScatterCost">' + ovScatter(lb, 'cost') + '</div></div></div>' +
    '<div class="tr-block"><div class="tr-block-head"><h3>Quality vs. time</h3><span class="tr-sub">up &amp; left is better · wall-clock per task</span></div>' +
    '<div class="tr-card"><div id="trScatterTime">' + ovScatter(lb, 'time') + '</div></div></div>' +
    '<div class="tr-block">' + routeBlock(byType, lb) + '</div>' +
    leaderboardBlock(lb) +
    runsBlock(runs) +
    '</section>'
  wireOverview(root)
  if (runs.some(isLive)) schedulePoll(root)
}

function filterBar(filters: any): string {
  const typeOpts = sortTypes(filters.taskTypes || []).map((t: string) => ({ v: t, label: typeLabel(t) }))
  const plain = (xs: string[]) => (xs || []).map((x) => ({ v: x, label: x }))
  return '<div class="tr-filterbar">' +
    msSelect('trFilterType', 'Task type', 'All types', 'types', typeOpts, state.tr.ltype) +
    msSelect('trFilterRepo', 'Repo', 'All repos', 'repos', plain(filters.repos), state.tr.lrepo) +
    msSelect('trFilterAgent', 'Agent', 'All agents', 'agents', plain(filters.agents), state.tr.lagent) +
    '<span class="tr-fb-count">' + lb0Count() + '</span></div>'
}
function lb0Count(): string {
  const d = ovCache || {}
  const runs = (d.runs || []).length, lb = (d.leaderboard || []).length
  const cells = (d.leaderboard || []).reduce((a: number, v: any) => a + (v.cells || 0), 0)
  return runs + ' runs · ' + lb + ' variants · ' + num(cells) + ' cells'
}

// -- recommendations (deterministic) --
function recoBlock(lb: any[]): string {
  const scored = lb.filter((v) => v.mean != null)
  if (!scored.length) return ''
  const quality = scored.slice().sort((a, b) => (b.mean || 0) - (a.mean || 0))[0]
  const value = scored.filter((v) => v.meanCost && v.meanCost > 0).sort((a, b) => (b.mean / b.meanCost) - (a.mean / a.meanCost))[0]
  const fastest = scored.filter((v) => v.meanDuration != null).sort((a, b) => a.meanDuration - b.meanDuration)[0]
  const card = (kind: string, cls: string, v: any, big: string, unit: string, why: string) => v
    ? '<div class="tr-reco ' + cls + '"><div class="tr-reco-kind">' + esc(kind) + '</div>' +
      '<div class="tr-reco-model">' + esc(shortModel(v.model)) + '</div>' +
      '<div class="tr-reco-agent">' + esc(providerOf(v.model)) + ' · ' + esc(v.agent) + '</div>' +
      '<div class="tr-reco-big"><span class="tr-reco-v">' + big + '</span><span class="tr-reco-u">' + esc(unit) + '</span></div>' +
      '<div class="tr-reco-why">' + why + '</div>' +
      '<div class="tr-reco-spark">' + sparkline(v.spark || []) + '</div></div>'
    : ''
  const mult = (quality && value && value.meanCost) ? Math.round(quality.meanCost / value.meanCost) : 0
  const cards =
    card('Best quality', 'q', quality, pct(quality.mean), 'mean · ' + pct(quality.resolvedRate) + ' resolved',
      'Top mean across ' + quality.runs + ' runs / ' + quality.cells + ' cells — the safe pick when quality matters most.') +
    card('Best value', 'v', value, pct(value && value.mean), 'at ' + (value ? usd(value.meanCost) : '—') + '/task',
      value ? pct(value.mean) + ' of the quality at ' + (mult ? '~1/' + mult + ' the cost of ' + shortModel(quality.model) : 'a fraction of the cost') + '. Route bulk work here.' : '') +
    card('Fastest', 'f', fastest, fmtDur(fastest && fastest.meanDuration), '/task · ' + pct(fastest && fastest.mean) + ' mean',
      'Lowest wall-clock per task among scored variants — best for latency-sensitive routing.')
  const summary = (quality && value)
    ? '<div class="tr-reco-summary"><b>' + esc(shortModel(quality.model)) + '</b> leads on quality (' + pct(quality.mean) + ' mean, ' +
      pct(quality.resolvedRate) + ' resolved), but <b>' + esc(shortModel(value.model)) + '</b> delivers <b>' + pct(value.mean) +
      '</b> at <b>' + usd(value.meanCost) + '/task</b>' + (mult ? ' — roughly 1/' + mult + ' the cost' : '') +
      '. The routing win is per task type, below.</div>'
    : ''
  return '<div class="tr-block"><div class="tr-block-head"><h3>Recommendation</h3><span class="tr-sub">across all done runs</span></div>' +
    '<div class="tr-recos">' + cards + '</div>' + summary + '</div>'
}

// L1 suite scatters: mean score against $/task or wall-clock/task, one point
// per variant. Both share the Pareto flag, so the toggle repaints both.
type OvAxis = 'cost' | 'time'
function lbScatter(lb: any[], axis: OvAxis): ScatterPt[] {
  const xOf = (v: any): number | null => (axis === 'cost' ? v.meanCost : v.meanDuration)
  return lb.filter((v) => v.mean != null && xOf(v) != null && (xOf(v) as number) > 0)
    .map((v) => ({ key: v.agent + '|' + v.model, label: shortModel(v.model), x: xOf(v) as number, y: v.mean, size: v.cells || 0 }))
}
function ovScatter(lb: any[], axis: OvAxis): string {
  return axis === 'cost'
    ? scatterSvg(lbScatter(lb, 'cost'), (v) => usd(v), 'Cost / task', { W: 960, H: 300, pareto: scPareto })
    : lbScatter(lb, 'time').length
      ? scatterSvg(lbScatter(lb, 'time'), fmtDur, 'Time / task', { W: 960, H: 300, pareto: scPareto })
      : '<div class="empty">No run-time data for this suite — its runs predate per-cell timing. New runs record it.</div>'
}
function scatterBlock(title: string, hint: string, points: ScatterPt[], xFmt?: (v: number) => string, xLabel?: string, opts?: { W?: number; H?: number; yLabel?: string; pareto?: boolean }): string {
  return '<div class="tr-card"><div class="tr-card-h2"><h3>' + esc(title) + '</h3><span class="tr-hint">' + hint + '</span></div>' +
    scatterSvg(points, xFmt || ((v) => usd(v)), xLabel || 'Cost / task', opts) + '</div>'
}

// -- route-by-type heatmap --
function routeBlock(byType: any[], lb: any[]): string {
  if (!byType.length) return '<div class="tr-card"><div class="tr-card-h2"><h3>Route by task type</h3></div><div class="empty">No scored types yet.</div></div>'
  const map = new Map<string, number>()
  byType.forEach((r) => { if (r.mean != null) map.set(r.taskType + '|' + r.agent + '|' + r.model, r.mean) })
  const modelsWith = new Set(byType.map((r) => r.agent + '|' + r.model))
  const cols: HmCol[] = lb.filter((v) => modelsWith.has(v.agent + '|' + v.model))
    .map((v) => ({ key: v.agent + '|' + v.model, label: shortModel(v.model) }))
  const rows: HmRow[] = TYPES.filter((t) => byType.some((r) => r.taskType === t.key)).map((t) => ({ key: t.key, label: t.label }))
  const grid = heatmap(rows, cols, (rk, ck) => { const v = map.get(rk + '|' + ck); return v == null ? null : v }, 'Task type')
  return '<div class="tr-card"><div class="tr-card-h2"><h3>Route by task type</h3><span class="tr-hint">best model per kind of work</span></div>' +
    '<div class="tr-route-line">Premium models (Opus/Sonnet) tend to pull ahead only on <b>Implement / Review / Testing</b>; ' +
    'cheaper models hold their own on <b>Explore / Plan / Email / Doc</b>.</div>' + grid +
    '<div class="tr-hm-legend"><span>lower</span><span class="tr-hm-ramp"></span><span>higher</span><span class="tr-hm-legsp">▢ best in row</span></div></div>'
}

// -- leaderboard (sortable + searchable, client-side) --
const LB_COLS: { key: string; label: string; align: string; get: (v: any) => number | null }[] = [
  { key: 'mean', label: 'Mean score', align: 'l', get: (v) => v.mean },
  { key: 'resolvedRate', label: 'Resolved', align: 'r', get: (v) => v.resolvedRate },
  { key: 'value', label: 'Value', align: 'r', get: (v) => (v.meanCost ? v.mean / v.meanCost : null) },
  { key: 'meanCost', label: '$/task', align: 'r', get: (v) => v.meanCost },
  { key: 'meanTokens', label: 'Tokens', align: 'r', get: (v) => v.meanTokens },
  { key: 'meanDuration', label: 'Time', align: 'r', get: (v) => v.meanDuration },
  { key: 'cells', label: 'Cells', align: 'r', get: (v) => v.cells },
]
function leaderboardBlock(lb: any[]): string {
  return '<div class="tr-block"><div class="tr-block-head"><h3>Model leaderboard</h3>' +
    '<span class="tr-sub">sortable · click a header</span>' +
    '<input class="tr-search tr-lb-search" id="trLbSearch" placeholder="Search a model…" value="' + esc(lbQuery) + '"></div>' +
    '<div class="tr-card tr-card-flat"><div id="trLbWrap">' + lbTable(lb) + '</div>' +
    '<div class="tr-note">Value = mean score ÷ $/task. Scores are calibration-grade and relative — read them for comparison, not as absolute capability.</div></div></div>'
}
function lbTable(lb: any[]): string {
  let rows = lb.slice()
  if (lbQuery) rows = rows.filter((v) => (v.variant + ' ' + v.model).toLowerCase().indexOf(lbQuery.toLowerCase()) >= 0)
  const col = LB_COLS.filter((c) => c.key === lbSort)[0] || LB_COLS[0]!
  rows.sort((a, b) => { const av = col.get(a), bv = col.get(b); return ((av == null ? -Infinity : av) - (bv == null ? -Infinity : bv)) * lbDir })
  const bestVal = Math.max.apply(null, lb.map((v) => (v.meanCost ? v.mean / v.meanCost : 0)))
  const fmt: Record<string, (v: any) => string> = {
    mean: (v) => scoreBar(v.mean), resolvedRate: (v) => pct(v.resolvedRate),
    value: (v) => v.meanCost ? '<span class="' + (v.mean / v.meanCost === bestVal ? 'tr-val-best' : '') + '">' + (v.mean / v.meanCost).toFixed(1) + '</span>' : '—',
    meanCost: (v) => v.meanCost != null ? usd(v.meanCost) : '—', meanTokens: (v) => fmtTok(v.meanTokens),
    meanDuration: (v) => fmtDur(v.meanDuration), cells: (v) => num(v.cells),
  }
  const head = '<thead><tr><th class="tr-lb-rank">#</th><th class="tr-lb-variant">Variant</th>' +
    LB_COLS.map((c) => '<th class="' + (c.align === 'r' ? 'r ' : '') + (c.key === lbSort ? 'tr-sorted' : '') + '" data-sort="' + c.key + '">' +
      esc(c.label) + ' <span class="tr-ar">' + (c.key === lbSort ? (lbDir < 0 ? '▼' : '▲') : '▲') + '</span></th>').join('') +
    '</tr></thead>'
  const body = '<tbody>' + rows.map((v, i) =>
    '<tr><td class="tr-lb-rank">' + (i + 1) + '</td>' +
    '<td class="tr-lb-variant"><span class="tr-lb-name">' + esc(shortModel(v.model)) + '</span>' +
    (i === 0 && lbSort === 'mean' && lbDir < 0 ? '<span class="tr-tag-best">top</span>' : '') +
    '<div class="tr-lb-sub">' + esc(providerOf(v.model)) + ' · ' + esc(v.agent) + '</div></td>' +
    LB_COLS.map((c) => '<td class="' + (c.align === 'r' ? 'r' : 'tr-lb-scorecell') + '">' + fmt[c.key]!(v) + '</td>').join('') +
    '</tr>').join('') + '</tbody>'
  return '<table class="tr-lb2">' + head + body + '</table>'
}

function runsBlock(runs: any[]): string {
  const rows = runs.map((r) => {
    const prog = isLive(r) ? progressBar(r.done, r.total) : (r.total ? '<span class="tr-prog-done">' + num(r.done) + ' / ' + num(r.total) + '</span>' : '—')
    return '<tr class="tr-runrow" data-run="' + esc(r.id) + '">' +
      '<td class="tr-run-label">' + esc(r.label || r.id) + '</td>' +
      '<td>' + statusPill(r.status) + '</td>' +
      '<td class="tr-run-metric num">' + r.tasks + ' × ' + r.variants + '</td>' +
      '<td class="tr-run-metric tr-run-prog">' + prog + '</td>' +
      '<td class="tr-run-metric tr-run-mean">' + (r.mean != null ? scoreBar(r.mean) : '—') + '</td>' +
      '<td class="tr-when">' + esc(when(r.createdAt)) + '</td><td class="exp-go">→</td></tr>'
  }).join('')
  return '<div class="tr-block"><div class="tr-block-head"><h3>Recent runs</h3><span class="tr-sub">latest sweeps</span></div>' +
    '<div class="tr-card tr-card-flat"><table class="tr-runs"><thead><tr><th>Label</th><th>Status</th>' +
    '<th class="tr-run-metric">Tasks × variants</th><th class="tr-run-metric">Progress</th><th class="tr-run-metric">Mean</th><th>When</th><th></th></tr></thead>' +
    '<tbody>' + rows + '</tbody></table></div></div>'
}

function wireOverview(root: HTMLElement): void {
  Array.prototype.forEach.call(root.querySelectorAll('.tr-runrow'), (el: HTMLElement) => { el.onclick = () => goRun(el.getAttribute('data-run') || '') })
  // Server-side filters: apply once, when the dropdown closes (not per tick).
  const refilter = (key: 'ltype' | 'lrepo' | 'lagent') => (vals: string[]) => {
    state.tr[key] = vals; syncHash({ replace: true }); renderOverview(root, false)
  }
  wireMs(root, 'trFilterType', refilter('ltype'))
  wireMs(root, 'trFilterRepo', refilter('lrepo'))
  wireMs(root, 'trFilterAgent', refilter('lagent'))
  const nr = root.querySelector('#trNewRun') as HTMLElement | null
  if (nr) nr.onclick = () => { const s = (ovCache || {}).suite; openDialog({ suiteId: state.tr.suite, suiteName: s?.name || 'Suite', suiteTasks: s?.tasks || 0 }) }
  const s = root.querySelector('#trLbSearch') as HTMLInputElement | null
  if (s) s.oninput = () => { lbQuery = s.value; const w = root.querySelector('#trLbWrap') as HTMLElement | null; if (w) { w.innerHTML = lbTable((ovCache || {}).leaderboard || []); wireLb(root) } }
  const pb = root.querySelector('#trParetoBtn') as HTMLElement | null
  if (pb) pb.onclick = () => {
    scPareto = !scPareto
    pb.classList.toggle('on', scPareto); pb.setAttribute('aria-checked', String(scPareto))
    const lb = (ovCache || {}).leaderboard || []
    ;(['cost', 'time'] as OvAxis[]).forEach((axis) => {
      const w = root.querySelector(axis === 'cost' ? '#trScatterCost' : '#trScatterTime') as HTMLElement | null
      if (w) w.innerHTML = ovScatter(lb, axis)
    })
    wireLb(root); wireTips(root)
  }
  wireLb(root)
  wireTips(root)
  wireBack(root)
}
function wireLb(root: HTMLElement): void {
  Array.prototype.forEach.call(root.querySelectorAll('#trLbWrap th[data-sort]'), (th: HTMLElement) => {
    th.onclick = () => {
      const k = th.getAttribute('data-sort')!
      if (lbSort === k) lbDir *= -1; else { lbSort = k; lbDir = -1 }
      const w = root.querySelector('#trLbWrap') as HTMLElement | null
      if (w) { w.innerHTML = lbTable((ovCache || {}).leaderboard || []); wireLb(root) }
    }
  })
  // scatter point / legend click → filter the leaderboard to that variant
  Array.prototype.forEach.call(root.querySelectorAll('.tr-sc-dot, .tr-scatter-vkey'), (el: HTMLElement) => {
    el.addEventListener('click', () => {
      const key = el.getAttribute('data-key') || ''
      const model = key.split('|')[1] || ''
      lbQuery = shortModel(model)
      const s = root.querySelector('#trLbSearch') as HTMLInputElement | null
      if (s) s.value = lbQuery
      const w = root.querySelector('#trLbWrap') as HTMLElement | null
      if (w) { w.innerHTML = lbTable((ovCache || {}).leaderboard || []); wireLb(root) }
    })
  })
}

// ==== L1 run page =============================================================

function renderRun(root: HTMLElement, runId: string, background: boolean): void {
  if (!background && !runCache.has(runId)) root.innerHTML = '<div class="empty">Loading run…</div>'
  get('/api/task-replay/run?run=' + encodeURIComponent(runId) + '&_=' + Date.now())
    .then((data: any) => { runCache.set(runId, data); if (state.view === 'taskreplay' && state.tr.run === runId && !state.tr.sel) paintRun(root, runId) })
    .catch(() => {
      if (!background) {
        root.innerHTML = '<section class="panel"><div class="exp-detail-head">' + overviewBack() + '<h2>Run not found</h2></div><div class="empty">This run isn’t in the results.</div></section>'
        wireBack(root)
      }
    })
}

function paintRun(root: HTMLElement, runId: string): void {
  const data = runCache.get(runId)
  if (!data) return
  const r = data.run, s = data.summary || {}
  // Deep-link into a run without a suite in state: adopt the run's own suite so
  // the back-nav lands on the right suite (kept out of the URL until navigation).
  if (!state.tr.suite && r.suiteId) state.tr.suite = r.suiteId
  const prog = isLive(r) ? progressBar(r.done, r.total) : num(r.done) + ' / ' + num(r.total) + ' cells'
  const params = r.params || {}
  const sub = [
    params.contextMode ? 'prior context: ' + esc(String(params.contextMode)) : '',
    params.effort ? 'effort: ' + esc(String(params.effort)) : '',
    r.error ? '<span class="tr-err">error: ' + esc(String(r.error)) + '</span>' : '',
  ].filter(Boolean).join(' · ')
  const rerun = (params.variants || params.tasks) ? '<button type="button" class="btn tr-rerun" id="trRerun" title="New run pre-filled with this configuration">Re-run</button>' : ''
  const cancel = isLive(r) ? '<button type="button" class="btn tr-cancelrun" id="trCancelRun">Cancel</button>' : ''
  const resume = (!isLive(r) && r.done < r.total) ? '<button type="button" class="btn tr-resumerun" id="trResumeRun" title="Re-queue the unfinished cells">Resume</button>' : ''

  const variants = (s.byVariant || []) as any[]
  // The scatter's type pick is kept across polls; drop types this run doesn't have.
  scTypes = msNormalize(scTypes, sortTypes((data.tasks || []).map((t: any) => t.taskType).filter(Boolean)).map((v) => ({ v, label: v })), true)

  root.innerHTML =
    '<section class="tr-l1"><div class="exp-detail-head">' + overviewBack() +
    '<h2>' + esc(r.label || r.id) + '</h2>' + statusPill(r.status) +
    '<span class="tr-run-headprog">' + r.tasks + ' × ' + r.variants + ' · ' + prog + '</span>' + rerun + resume + cancel + '</div>' +
    (sub ? '<div class="tr-run-subhead">' + sub + '</div>' : '') +
    kpiStrip(s, variants) +
    '<div class="tr-block"><div class="tr-block-head"><h3>Cost &amp; speed trade-off</h3><span class="tr-sub">each point = a variant · up-and-left is better</span></div>' +
    scatterControls(data.tasks || []) + '<div class="tr-twoup" id="trScWrap">' + scatterPair(data.tasks || []) + '</div></div>' +
    '<div class="tr-block"><div class="tr-block-head"><h3>Score by task type × variant</h3><span class="tr-sub">top row = mean across all tasks</span></div>' +
    '<div class="tr-card">' + runHeatmap(s, variants) + '<div class="tr-hm-legend"><span>lower</span><span class="tr-hm-ramp"></span><span>higher</span><span class="tr-hm-legsp">▢ best in row</span></div></div></div>' +
    matrixSection(data.tasks || [], variants) +
    '</section>'
  wireBack(root)
  wireScatter(root, runId)
  wireMatrix(root, runId)
  wireTips(root)
  const rr = root.querySelector('#trRerun') as HTMLElement | null
  if (rr) rr.onclick = () => openDialog(rerunPrefill(r))
  const cx = root.querySelector('#trCancelRun') as HTMLElement | null
  if (cx) cx.onclick = () => { cx.setAttribute('disabled', 'true'); cx.textContent = 'Cancelling…'; postStatus('/api/task-replay/runs/' + encodeURIComponent(runId) + '/cancel', {}).then(() => { runCache.delete(runId); renderRun(root, runId, false) }) }
  const rz = root.querySelector('#trResumeRun') as HTMLElement | null
  if (rz) rz.onclick = () => { rz.setAttribute('disabled', 'true'); rz.textContent = 'Resuming…'; postStatus('/api/task-replay/runs/' + encodeURIComponent(runId) + '/resume', {}).then(() => { runCache.delete(runId); renderRun(root, runId, false) }) }
  if (isLive(r)) schedulePoll(root)
}

function kpiStrip(s: any, variants: any[]): string {
  const verdicts = (s.verdicts || []) as { verdict: string; n: number }[]
  const total = verdicts.reduce((a, v) => a + v.n, 0) || 1
  const scored = verdicts.filter((v) => v.verdict !== 'unscored').reduce((a, v) => a + v.n, 0)
  const seg = verdicts.map((v) => '<span class="tr-vseg ' + verdictClass(v.verdict) + '" style="flex:' + v.n + '"></span>').join('')
  const leg = verdicts.map((v) => '<span class="tr-vkey"><span class="tr-vdot ' + verdictClass(v.verdict) + '"></span>' + esc(v.verdict) + ' ' + v.n + '</span>').join('')
  // mean over scored cells
  const meanRow = variants.filter((v) => v.mean != null)
  const mean = meanRow.length ? meanRow.reduce((a, v) => a + v.mean * v.cells, 0) / meanRow.reduce((a, v) => a + v.cells, 0) : null
  const top = variants.filter((v) => v.mean != null)[0]
  const costSplit = (s.agentCost != null || s.judgeCost != null)
    ? '<div class="tr-cost-split"><span>agent ' + (s.agentCost != null ? usd(s.agentCost) : '—') + '</span><span>judge ' + (s.judgeCost != null ? usd(s.judgeCost) : '—') + '</span>' +
      (s.harnessCost != null ? '<span>harness ' + usd(s.harnessCost) + '</span>' : '') + '</div>' : ''
  return '<div class="tr-kpis">' +
    '<div class="tr-kpi"><div class="tr-kpi-k">Verdicts</div><div class="tr-vbar">' + seg + '</div><div class="tr-vlegend">' + leg + '</div></div>' +
    '<div class="tr-kpi"><div class="tr-kpi-k">Mean score</div><div class="tr-kpi-v">' + pct(mean) + '</div><div class="tr-kpi-sub">' + scored + ' scored cells</div></div>' +
    '<div class="tr-kpi"><div class="tr-kpi-k">Total cost</div><div class="tr-kpi-v">' + (s.totalCost != null ? usd(s.totalCost) : '—') + '</div>' + costSplit + '</div>' +
    '<div class="tr-kpi"><div class="tr-kpi-k">Top variant</div><div class="tr-kpi-v tr-kpi-model">' + (top ? esc(shortModel(top.model)) : '—') + '</div>' +
    (top ? '<div class="tr-kpi-sub">' + esc(providerOf(top.model)) + ' · ' + pct(top.mean) + ' mean</div>' : '') + '</div>' +
    '</div>'
}

// -- L1 cost/speed scatters (faceted by task type, quality metric switchable) --
// Two scatters kept SIDE BY SIDE on purpose: they share the Y axis (quality) and
// differ only in X (cost vs wall-clock), so a variant's position reads across both
// at a glance — stacking would force a scroll to compare the same point.
function scatterControls(tasks: any[]): string {
  const present = sortTypes(tasks.map((t) => t.taskType).filter(Boolean))
  const metricBtns = ([['score', 'Mean score'], ['resolve', 'Resolve rate']] as [string, string][])
    .map(([k, l]) => '<button data-m="' + k + '"' + (scMetric === k ? ' class="on"' : '') + '>' + l + '</button>').join('')
  return '<div class="tr-mx-controls tr-sc-controls">' +
    msSelect('trScType', 'Task type', 'All task types', 'types', present.map((t) => ({ v: t, label: typeLabel(t) })), scTypes, { dropUnknown: true }) +
    '<span class="tr-ctl-lab">Y axis</span><span class="tr-seg" id="trScMetric">' + metricBtns + '</span>' + paretoToggle() + '</div>'
}
/** Per-variant cost/quality point over the tasks of the selected types ([] = all),
 *  aggregated from the run's cells so any combination of types is exact (means of
 *  per-type means would mis-weight). Each mean skips cells missing that measure. */
function scatterStats(tasks: any[], types: string[]): any[] {
  const keep = types.length ? new Set(types) : null
  const by = new Map<string, any>()
  for (const t of tasks) {
    if (keep && !keep.has(t.taskType)) continue
    for (const c of (t.results || []) as any[]) {
      const k = c.agent + '|' + c.model
      let a = by.get(k)
      if (!a) { a = { agent: c.agent, model: c.model, score: 0, scored: 0, resolved: 0, cost: 0, nCost: 0, dur: 0, nDur: 0 }; by.set(k, a) }
      if (c.score != null) { a.score += c.score; a.scored++; if (c.verdict === 'resolved') a.resolved++ }
      if (c.costUsd != null) { a.cost += c.costUsd; a.nCost++ }
      if (c.durationSecs != null) { a.dur += c.durationSecs; a.nDur++ }
    }
  }
  return [...by.values()].map((a) => ({
    agent: a.agent, model: a.model, scored: a.scored,
    meanScore: a.scored ? a.score / a.scored : null,
    resolveRate: a.scored ? a.resolved / a.scored : null,
    meanCost: a.nCost ? a.cost / a.nCost : null,
    meanDuration: a.nDur ? a.dur / a.nDur : null,
  }))
}
function scatterPair(tasks: any[]): string {
  const rows = scatterStats(tasks, scTypes)
  const yOf = (x: any) => scMetric === 'resolve' ? x.resolveRate : x.meanScore
  const yLabel = scMetric === 'resolve' ? 'Resolve rate' : 'Mean score'
  const cost: ScatterPt[] = rows.filter((x: any) => yOf(x) != null && x.meanCost != null && x.meanCost > 0)
    .map((x: any) => ({ key: x.agent + '|' + x.model, label: shortModel(x.model), x: x.meanCost, y: yOf(x), size: x.scored || 0 }))
  const time: ScatterPt[] = rows.filter((x: any) => yOf(x) != null && x.meanDuration != null && x.meanDuration > 0)
    .map((x: any) => ({ key: x.agent + '|' + x.model, label: shortModel(x.model), x: x.meanDuration, y: yOf(x), size: x.scored || 0 }))
  return scatterBlock('Quality vs. cost', '$/task', cost, undefined, undefined, { yLabel, pareto: scPareto }) +
    scatterBlock('Quality vs. time', 'wall-clock/task', time, fmtDur, 'Time / task', { yLabel, pareto: scPareto })
}
function wireScatter(root: HTMLElement, runId: string): void {
  const rerender = () => {
    const data = runCache.get(runId); if (!data) return
    const w = root.querySelector('#trScWrap') as HTMLElement | null
    if (w) { w.innerHTML = scatterPair(data.tasks || []); wireTips(root) }
  }
  // Client-side over the cached grid — cheap, so it re-plots live as boxes tick.
  wireMs(root, 'trScType', (vals) => { scTypes = vals; rerender() }, { live: true })
  Array.prototype.forEach.call(root.querySelectorAll('#trScMetric button'), (b: HTMLElement) => {
    b.onclick = () => {
      scMetric = (b.getAttribute('data-m') as 'score' | 'resolve') || 'score'
      Array.prototype.forEach.call(root.querySelectorAll('#trScMetric button'), (x: HTMLElement) => x.classList.remove('on'))
      b.classList.add('on'); rerender()
    }
  })
  const pb = root.querySelector('#trParetoBtn') as HTMLElement | null
  if (pb) pb.onclick = () => { scPareto = !scPareto; pb.classList.toggle('on', scPareto); pb.setAttribute('aria-checked', String(scPareto)); rerender() }
}

function runHeatmap(s: any, variants: any[]): string {
  const byType = (s.byType || []) as any[]
  const cols: HmCol[] = variants.map((v) => ({ key: v.agent + '|' + v.model, label: shortModel(v.model), sub: providerOf(v.model) }))
  const tmap = new Map<string, number>()
  byType.forEach((r) => { if (r.mean != null) tmap.set(r.taskType + '|' + r.agent + '|' + r.model, r.mean) })
  const vmap = new Map<string, number>()
  variants.forEach((v) => { if (v.mean != null) vmap.set(v.agent + '|' + v.model, v.mean) })
  const rows: HmRow[] = [{ key: '__all__', label: 'All tasks', emph: true }].concat(
    TYPES.filter((t) => byType.some((r) => r.taskType === t.key)).map((t) => ({ key: t.key, label: t.label })))
  return heatmap(rows, cols, (rk, ck) => rk === '__all__' ? (vmap.has(ck) ? vmap.get(ck)! : null) : (tmap.has(rk + '|' + ck) ? tmap.get(rk + '|' + ck)! : null), 'Task type')
}

// -- task × variant matrix --
function matrixSection(tasks: any[], variants: any[]): string {
  return '<div class="tr-block"><div class="tr-block-head"><h3>Every task × variant</h3><span class="tr-sub">click a cell → transcript · diff · judge</span></div>' +
    '<div class="tr-mx-controls">' +
    '<input class="tr-search" id="trMxSearch" placeholder="Search a task…" value="' + esc(mxQuery) + '">' +
    '<button type="button" class="tr-attn' + (mxAttn ? ' on' : '') + '" id="trMxAttn">⚠ Needs attention</button>' +
    '<span class="tr-ctl-lab">Sort</span><span class="tr-seg" id="trMxSort">' +
    ['type', 'disagree', 'mean'].map((k) => '<button data-s="' + k + '"' + (mxSort === k ? ' class="on"' : '') + '>' + (k === 'type' ? 'By type' : k === 'disagree' ? 'Most disagreement' : 'Lowest mean') + '</button>').join('') +
    '</span><span class="tr-mx-count" id="trMxCount"></span></div>' +
    '<div class="tr-mx-card" id="trMxWrap">' + matrixTable(tasks, variants) + '</div>' +
    '<div class="tr-hm-legend"><span>lower</span><span class="tr-hm-ramp"></span><span>higher score</span>' +
    '<span class="tr-mx-unrkey"><i></i>red edge = unresolved (0)</span></div></div>'
}
function matrixTable(tasks: any[], variants: any[]): string {
  const cols = variants
  // per-task stats
  const withStats = tasks.map((t) => {
    const byV = new Map<string, any>()
    ;(t.results || []).forEach((r: any) => byV.set(r.agent + '|' + r.model, r))
    const sc = cols.map((v) => byV.get(v.agent + '|' + v.model)).filter((r) => r && r.score != null).map((r) => r.score)
    const min = sc.length ? Math.min.apply(null, sc) : 0, max = sc.length ? Math.max.apply(null, sc) : 0
    const unr = cols.filter((v) => (byV.get(v.agent + '|' + v.model) || {}).verdict === 'unresolved').length
    return { t, byV, mean: sc.length ? sc.reduce((a, b) => a + b, 0) / sc.length : 0, min, max, range: max - min, unr }
  })
  let rows = withStats
  if (mxQuery) rows = rows.filter((x) => (x.t.title || x.t.id).toLowerCase().indexOf(mxQuery.toLowerCase()) >= 0)
  if (mxAttn) rows = rows.filter((x) => x.unr > 0 || x.min < 0.5)
  const tyIdx = (ty: string) => { const i = TYPES.findIndex((x) => x.key === ty); return i < 0 ? 99 : i }
  if (mxSort === 'type') rows = rows.slice().sort((a, b) => tyIdx(a.t.taskType) - tyIdx(b.t.taskType) || a.mean - b.mean)
  else if (mxSort === 'disagree') rows = rows.slice().sort((a, b) => b.range - a.range)
  else rows = rows.slice().sort((a, b) => a.mean - b.mean)

  // Two columns can share a model (same model, different agents): name the agent
  // under the provider whenever the run mixes agents, so no two headers read alike.
  const multiAgent = new Set(cols.map((v) => v.agent)).size > 1
  const head = '<thead><tr><th class="tr-mx-htask">Task</th>' +
    cols.map((v) => '<th class="tr-mx-hcol" data-tip="' + esc(v.model + '\n' + v.agent + ' · ' + providerOf(v.model)) + '"><span class="tr-mx-cm">' + esc(shortModel(v.model)) + '</span>' +
      '<span class="tr-mx-cs">' + esc(providerOf(v.model) + (multiAgent ? ' · ' + v.agent : '')) + '</span><span class="tr-mx-cmean">' + pct(v.mean) + '</span></th>').join('') + '</tr></thead>'
  let body = '<tbody>', lastTy: string | null = null
  if (!rows.length) body += '<tr><td class="tr-mx-none" colspan="' + (cols.length + 1) + '"><span class="tr-mx-glab">No tasks match.</span></td></tr>'
  rows.forEach((x) => {
    if (mxSort === 'type' && x.t.taskType !== lastTy) {
      lastTy = x.t.taskType
      const n = rows.filter((r) => r.t.taskType === lastTy).length
      // The label, not the full-width cell, is sticky: a cell as wide as the table
      // has nowhere to stick, so the label scrolled out with the columns.
      body += '<tr class="tr-mx-grp"><td colspan="' + (cols.length + 1) + '"><span class="tr-mx-glab">' + esc(typeLabel(lastTy)) + '<span class="tr-mx-gc">' + n + ' task' + (n > 1 ? 's' : '') + '</span></span></td></tr>'
    }
    body += '<tr><td class="tr-mx-task"><span class="tr-mx-title" title="' + esc(x.t.title || x.t.id) + '">' + esc(x.t.title || '(untitled)') + '</span>' +
      '<span class="tr-mx-meta">' + esc(x.t.repo || '') + (x.t.turnStart != null ? ' · #' + x.t.turnStart + '–#' + x.t.turnEnd : '') + '</span></td>' +
      cols.map((v) => {
        const c = x.byV.get(v.agent + '|' + v.model)
        if (!c) return '<td class="tr-mx-cell tr-mx-empty">·</td>'
        if (c.status === 'running') return '<td class="tr-mx-cell tr-mx-empty"><span class="tr-mx-st tr-mx-running"><span class="bench-spin"></span>running</span></td>'
        if (c.status === 'queued') return '<td class="tr-mx-cell tr-mx-empty"><span class="tr-mx-st">queued</span></td>'
        // A failed cell opens like any other — its page shows the full failure.
        if (isFailed(c.status)) return '<td class="tr-mx-cell tr-mx-empty tr-mx-failcell" data-cell="' + esc(x.t.id + '|' + v.agent + '|' + v.model) + '" data-tip="' + esc(shortModel(v.model) + '\n' + (c.stoppedReason || c.status) + '\nclick for details') + '"><span class="tr-mx-st tr-mx-fail">' + (c.status === 'error' ? 'error' : 'no trial') + '</span></td>'
        if (c.score == null) return '<td class="tr-mx-cell tr-mx-empty" data-tip="' + esc(shortModel(v.model) + '\n' + (c.verdict || c.status)) + '">–</td>'
        const unr = c.verdict === 'unresolved' ? ' tr-mx-unr' : ''
        const tip = shortModel(v.model) + ' · ' + (x.t.title || '') + '\n' + (c.verdict || '') + ' · ' + (c.costUsd != null ? usd(c.costUsd) : '—') + ' · ' + (c.diffStat || 'no changes')
        return '<td class="tr-mx-cell' + unr + '" style="background:' + scoreColor(c.score) + '" data-cell="' + esc(x.t.id + '|' + v.agent + '|' + v.model) + '" data-tip="' + esc(tip) + '">' + Math.round(c.score * 100) + '</td>'
      }).join('') + '</tr>'
  })
  body += '</tbody>'
  const bestKey = variants.filter((v) => v.mean != null).sort((a, b) => b.mean - a.mean)[0]
  const foot = '<tfoot><tr><td class="tr-mx-flab">Variant mean</td>' +
    cols.map((v) => '<td class="' + (bestKey && v.agent + '|' + v.model === bestKey.agent + '|' + bestKey.model ? 'tr-mx-fbest' : '') + '">' + pct(v.mean) + '</td>').join('') + '</tr></tfoot>'
  return '<table class="tr-mx" data-count="' + rows.length + '/' + tasks.length + '">' + head + body + foot + '</table>'
}
function wireMatrix(root: HTMLElement, runId: string): void {
  const rerender = () => {
    const data = runCache.get(runId); if (!data) return
    const w = root.querySelector('#trMxWrap') as HTMLElement | null
    if (w) { w.innerHTML = matrixTable(data.tasks || [], data.summary.byVariant || []); bindCells(); const tbl = w.querySelector('.tr-mx') as HTMLElement; const c = root.querySelector('#trMxCount'); if (c && tbl) c.textContent = tbl.getAttribute('data-count') + ' tasks'; wireTips(root) }
  }
  const bindCells = () => Array.prototype.forEach.call(root.querySelectorAll('.tr-mx-cell[data-cell]'), (el: HTMLElement) => { el.onclick = () => goCell(el.getAttribute('data-cell') || '') })
  bindCells()
  const tbl0 = root.querySelector('.tr-mx') as HTMLElement | null
  const c0 = root.querySelector('#trMxCount'); if (c0 && tbl0) c0.textContent = tbl0.getAttribute('data-count') + ' tasks'
  const s = root.querySelector('#trMxSearch') as HTMLInputElement | null
  if (s) s.oninput = () => { mxQuery = s.value; rerender() }
  const a = root.querySelector('#trMxAttn') as HTMLElement | null
  if (a) a.onclick = () => { mxAttn = !mxAttn; a.classList.toggle('on', mxAttn); rerender() }
  Array.prototype.forEach.call(root.querySelectorAll('#trMxSort button'), (b: HTMLElement) => {
    b.onclick = () => { mxSort = b.getAttribute('data-s') as any; Array.prototype.forEach.call(root.querySelectorAll('#trMxSort button'), (x: HTMLElement) => x.classList.remove('on')); b.classList.add('on'); rerender() }
  })
}

// ==== L2 cell detail ==========================================================

function paintDetail(root: HTMLElement, runId: string, selKey: string): void {
  const sel = parseSel(selKey)
  if (!sel) { goRun(runId); return }
  root.innerHTML = '<div class="empty">Loading cell…</div>'
  get('/api/task-replay/result?run=' + encodeURIComponent(runId) + '&task=' + encodeURIComponent(sel.task) +
      '&agent=' + encodeURIComponent(sel.agent) + '&model=' + encodeURIComponent(sel.model))
    .then((run: any) => renderDetail(root, runId, run))
    .catch(() => {
      root.innerHTML = '<section class="panel"><div class="exp-detail-head">' + runBack(runId) + '<h2>Cell not found</h2></div><div class="empty">This cell isn’t in the run.</div></section>'
      wireBack(root)
    })
}

function renderDetail(root: HTMLElement, runId: string, run: any): void {
  const t = run.task || {}, m = run.metrics || {}
  const checks = (run.checks || []) as any[]
  const failed = isFailed(run.status)
  const tiles = [
    failed
      ? tile('Status', '<span class="tr-mx-fail">' + (run.status === 'error' ? 'error' : 'no trial') + '</span>')
      : tile('Score', '<span class="tr-t-score">' + (run.score != null ? run.score : '—') + '</span> ' + verdictBadge(run.verdict)),
    tile('Agent cost', m.costUsd != null ? usd(m.costUsd) : '—'),
    tile('Judge cost', m.judgeCostUsd != null ? usd(m.judgeCostUsd) : '—'),
    ...(m.harnessCostUsd != null ? [tile('Harness cost', usd(m.harnessCostUsd), 'simulated user + environment')] : []),
    tile('Tokens', fmtTok(m.tokens)),
    tile('Time', fmtDur(m.durationSecs)),
    tile('Changes', '<span class="tr-t-changes">' + esc(run.diffStat || 'no changes') + '</span>'),
  ].join('')

  const critHtml = criteriaCard(checks)
  root.innerHTML =
    '<section class="panel"><div class="exp-detail-head">' + runBack(runId) +
    '<h2 class="tr-cell-model">' + esc(shortModel(run.model)) + '</h2>' +
    '<span class="tr-cell-type">' + esc(typeLabel(t.taskType)) + '</span></div>' +
    '<div class="tr-cell-task">' + esc(t.title || run.taskId) + (t.turnStart != null ? ' · <span class="mono">#' + t.turnStart + '–#' + t.turnEnd + '</span>' : '') + '</div>' +
    '<div class="tr-tiles">' + tiles + '</div></section>' +
    '<div class="tr-detail-body">' +
    // Row: judge verdict + criteria radar (criteria only when the judge scored them),
    // then the evidence tabs get the full width below.
    (failed ? failureCard(run)
      : '<div class="tr-judge-row' + (critHtml ? '' : ' tr-judge-solo') + '">' + judgeCard(run, checks) + critHtml + '</div>') +
    '<div class="tr-evidence">' + evidenceTabs(run, t) + '</div>' +
    '</div>'
  wireBack(root)
  wireTabs(root)
  const tx = root.querySelector('#trTxPane') as HTMLElement | null
  if (tx) wireTranscript(tx)
  wireTips(root)
}

/** A cell that produced nothing gradeable: no trial (the attempt never ran /
 *  never finished) or error (our collection/grading crashed on it). */
const isFailed = (status: string | null | undefined): boolean => status === 'no_trial' || status === 'error'

/** In place of the judge rail on a failed cell: what went wrong, and the captured
 *  exception / log tail. It's infrastructure, not a model result — excluded from
 *  every mean, and Resume re-runs it. */
function failureCard(run: any): string {
  const head = run.status === 'error'
    ? ['Grading failed', 'The agent produced a trial, but collecting or scoring it crashed.']
    : ['No trial', 'The attempt produced nothing gradeable — the sandbox or agent failed to start, or the run was stopped mid-trial.']
  return '<div class="tr-card tr-fail-card"><div class="tr-card-h2"><h3>' + head[0] + '</h3>' +
    '<span class="tr-hint">not counted in any score · <b>Resume</b> the run to retry</span></div>' +
    '<div class="tr-fail-sub">' + head[1] + '</div>' +
    (run.stoppedReason ? '<div class="tr-fail-reason mono">' + esc(run.stoppedReason) + '</div>' : '') +
    (run.failure
      ? '<pre class="tr-fail-pre">' + esc(run.failure) + '</pre>'
      : '<div class="empty">No further detail was captured for this cell.</div>') +
    '</div>'
}

function tile(k: string, v: string, sub?: string): string {
  return '<div class="tr-tile"><div class="tr-tile-k">' + esc(k) + '</div><div class="tr-tile-v">' + v + '</div>' + (sub ? '<div class="tr-tile-sub">' + esc(sub) + '</div>' : '') + '</div>'
}

/** Left rail card 1: gauge + score composition + judge panel + deterministic gate. */
function judgeCard(run: any, checks: any[]): string {
  const scored = checks.filter((c) => c.weight > 0 && !c.informational)
  const wsum = scored.reduce((a, c) => a + (c.weight || 0), 0)
  const comp = scored.length
    ? scored.map((c) => '<div class="tr-comp-row"><span class="tr-comp-name" title="' + esc(c.name) + '">' + esc(c.name.replace(/^judge:/, '').replace(/:.*$/, '').replace(/_/g, ' ')) + '</span>' +
        '<span class="tr-comp-w">' + (c.score != null ? c.score.toFixed(2) : '—') + ' × ' + c.weight + '</span>' +
        '<span class="tr-comp-bar"><i style="width:' + Math.round((c.score || 0) * 100) + '%;background:' + scoreColor(c.score) + '"></i></span></div>').join('') +
      '<div class="tr-comp-total"><span class="tr-comp-tl">weighted mean · Σw ' + wsum + '</span><span class="tr-comp-tv">' + (run.score != null ? run.score : '—') + '</span></div>' +
      '<div class="tr-comp-note"><code>' + (run.score != null ? run.score : '—') + '</code> = (' + scored.map((c) => (c.score != null ? c.score.toFixed(2) : '0') + '×' + c.weight).join(' + ') + ') ÷ ' + wsum +
        '. The criteria (radar, below) are the judge’s own sub-scores — audit only, not re-added.</div>'
    : '<div class="tr-comp-note">Scored directly by the judge (no weighted checks).</div>'
  // judge panel consensus, parsed from the judge check detail
  const judge = checks.filter((c) => c.kind === 'judge')[0]
  let panel = ''
  const pm = judge && /n=(\d+).*?=([\d.]+).*?range ([\d.]+)\D+([\d.]+)/.exec(judge.detail || '')
  if (pm) {
    const [, n, mu, lo, hi] = pm
    panel = '<div class="tr-panel"><span class="tr-panel-n">' + n + '-judge panel</span>' +
      '<span class="tr-panel-rng"><i style="left:' + (+lo! * 100) + '%;right:' + ((1 - +hi!) * 100) + '%"></i><b style="left:calc(' + (+mu! * 100) + '% - 1px)"></b></span>' +
      '<span class="tr-panel-mu">μ ' + mu + ' · ' + lo + '–' + hi + '</span></div>'
  }
  // deterministic gate
  const det = checks.filter((c) => c.kind === 'deterministic')
  const gate = det.length
    ? '<div class="tr-gate"><div class="tr-comp-lab">Deterministic gate</div><div class="tr-chips">' +
      det.map((c) => { const cls = c.weight === 0 ? 'info' : (c.passed ? 'pass' : 'fail'); return '<span class="tr-chip ' + cls + '" data-tip="' + esc(c.name + '\n' + (c.detail || '')) + '"><span class="tr-chip-dot"></span><span class="tr-chip-n">' + esc(c.name.split(':')[0]) + '</span><span class="tr-chip-v">' + (c.score != null ? c.score.toFixed(2) : '—') + '</span></span>' }).join('') +
      '</div></div>' : ''
  const gcol = run.score == null ? 'var(--gray)' : run.score >= 0.7 ? 'var(--emerald)' : run.score >= 0.4 ? 'var(--amber)' : 'var(--red)'
  const gauge = run.score != null ? donutGauge(run.score, gcol) : ''
  return '<div class="tr-card"><div class="tr-card-h2"><h3>Judge verdict</h3></div>' +
    '<div class="tr-verdict-top">' + gauge + '<div class="tr-vt-txt"><div class="tr-vt-score">' + (run.score != null ? run.score : '—') + '</div>' +
    '<div class="tr-vt-lab">' + verdictBadge(run.verdict) + ' verdict</div></div></div>' +
    '<div class="tr-comp"><div class="tr-comp-lab">Score composition</div>' + comp + '</div>' + panel + gate + '</div>'
}
function donutGauge(v: number, col: string): string {
  const r = 26, c = 2 * Math.PI * r, off = c * (1 - v)
  return '<svg class="tr-gauge" width="70" height="70" viewBox="0 0 70 70">' +
    '<circle cx="35" cy="35" r="' + r + '" fill="none" stroke="var(--track)" stroke-width="7"/>' +
    '<circle cx="35" cy="35" r="' + r + '" fill="none" stroke="' + col + '" stroke-width="7" stroke-linecap="round" stroke-dasharray="' + c + '" stroke-dashoffset="' + off + '" transform="rotate(-90 35 35)"/>' +
    '<text x="35" y="40" text-anchor="middle" class="tr-gauge-t">' + Math.round(v * 100) + '</text></svg>'
}

/** Sentence-case a rubric criterion name for display: "technical_approach" →
 *  "Technical approach". Backend keeps the raw snake_case; this is UI-only. */
function prettyCrit(name: string): string {
  const s = String(name || '').replace(/_/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

/** Left rail card 2: criteria radar (the judge's per-dimension sub-scores). */
function criteriaCard(checks: any[]): string {
  const crit = checks.filter((c) => c.kind === 'criterion')
  if (crit.length < 3) return ''
  // The viewBox reserves LABEL_PAD of horizontal room on each side of the diamond so
  // the longest criterion name (e.g. "Targets right behavior") sits fully inside the
  // box; the plot radius R is what's left. overflow:visible (CSS) is a safety net only.
  const LABEL_PAD = 160, R = 84, VH = 300, cy = 150, n = crit.length
  const cx = LABEL_PAD + R, VW = cx * 2
  const ang = (i: number) => -Math.PI / 2 + i * 2 * Math.PI / n
  const P = (val: number, i: number) => [cx + Math.cos(ang(i)) * R * val, cy + Math.sin(ang(i)) * R * val]
  let g = '<svg class="tr-radar" viewBox="0 0 ' + VW + ' ' + VH + '">'
  ;[0.25, 0.5, 0.75, 1].forEach((rr) => { g += '<polygon class="tr-radar-grid" points="' + crit.map((_, i) => { const [x, y] = P(rr, i); return x!.toFixed(1) + ',' + y!.toFixed(1) }).join(' ') + '"/>' })
  crit.forEach((_, i) => { const [x, y] = P(1, i); g += '<line class="tr-radar-axis" x1="' + cx + '" y1="' + cy + '" x2="' + x + '" y2="' + y + '"/>' })
  g += '<polygon class="tr-radar-poly" points="' + crit.map((c, i) => { const [x, y] = P(c.score || 0, i); return x!.toFixed(1) + ',' + y!.toFixed(1) }).join(' ') + '"/>'
  crit.forEach((c, i) => { const [x, y] = P(c.score || 0, i); g += '<circle class="tr-radar-pt" cx="' + x + '" cy="' + y + '" r="3"/>' })
  crit.forEach((c, i) => {
    const [lx, ly] = P(1.12, i)
    const anchor = Math.abs(Math.cos(ang(i))) < 0.3 ? 'middle' : (Math.cos(ang(i)) > 0 ? 'start' : 'end')
    g += '<text class="tr-radar-lab" x="' + lx + '" y="' + ly + '" text-anchor="' + anchor + '">' + esc(prettyCrit(c.name)) + '</text>'
    g += '<text class="tr-radar-val" x="' + lx + '" y="' + (ly! + 13) + '" text-anchor="' + anchor + '">' + (c.score != null ? c.score.toFixed(2) : '—') + ' <tspan class="tr-radar-w">(w' + c.weight + ')</tspan></text>'
  })
  g += '</svg>'
  return '<div class="tr-card"><div class="tr-card-h2"><h3>Judge criteria</h3><span class="tr-hint">weight in ()</span></div><div class="tr-radar-wrap">' + g + '</div></div>'
}

/** Right column: tabbed evidence, each pane in its own scroll box. */
const TR_TABS = [
  { key: 'analysis', label: 'Judge analysis' },
  { key: 'tx', label: 'Transcript' },
  { key: 'diff', label: 'Produced diff' },
  { key: 'ref', label: 'Reference' },
  { key: 'brief', label: 'Brief' },
]
function evidenceTabs(run: any, t: any): string {
  const txN = (run.transcript && run.transcript.length) ? run.transcript.length : 0
  const refLen = t.reference ? String(t.reference).length : 0
  const counts: Record<string, string> = {
    tx: txN ? String(txN) : '', diff: run.diffStat ? '' : '', ref: refLen ? (refLen >= 1000 ? Math.round(refLen / 1000) + 'k' : String(refLen)) : '',
  }
  if (!TR_TABS.some((x) => x.key === trTab)) trTab = 'analysis'
  const tabs = TR_TABS.map((x) => '<button class="tr-tab' + (x.key === trTab ? ' on' : '') + '" data-tab="' + x.key + '">' + esc(x.label) +
    (counts[x.key] ? '<span class="tr-tab-n">' + counts[x.key] + '</span>' : '') + '</button>').join('')

  const analysis = run.analysis ? '<div class="tr-prose">' + renderMd(run.analysis) + '</div>' : '<div class="empty">No judge analysis.</div>'
  const transcript = '<div id="trTxPane" class="tr-tx">' + renderTranscript(run.transcript || []) + '</div>'
  const diff = run.diff
    ? '<div class="tr-refhead mono">' + esc(run.diffStat || '') + '</div>' + ghDiff(run.diff)
    : '<div class="empty">No changes produced.</div>'
  const ref = t.reference
    ? '<div class="tr-refhead mono">ground truth · ' + esc(t.gtRef || '') + ' · ' + esc(t.gtKind || '') + '</div>' +
      (t.gtKind === 'diff' ? ghDiff(String(t.reference)) : '<div class="tr-prose">' + renderMd(String(t.reference)) + '</div>')
    : '<div class="empty">No reference recorded.</div>'
  const brief = t.instruction ? '<pre class="tr-brief">' + esc(t.instruction) + '</pre>' : '<div class="empty">No brief recorded.</div>'
  const pane = (key: string, html: string) => '<div class="tr-tabpane' + (key === trTab ? ' on' : '') + '" data-pane="' + key + '">' + html + '</div>'
  return '<div class="tr-tabs">' + tabs + '</div><div class="tr-tab-card">' +
    pane('analysis', analysis) + pane('tx', transcript) + pane('diff', diff) + pane('ref', ref) + pane('brief', brief) + '</div>'
}
function wireTabs(root: HTMLElement): void {
  Array.prototype.forEach.call(root.querySelectorAll('.tr-tab'), (b: HTMLElement) => {
    b.onclick = () => {
      trTab = b.getAttribute('data-tab') || 'analysis'
      Array.prototype.forEach.call(root.querySelectorAll('.tr-tab'), (x: HTMLElement) => x.classList.toggle('on', x === b))
      Array.prototype.forEach.call(root.querySelectorAll('.tr-tabpane'), (p: HTMLElement) => p.classList.toggle('on', p.getAttribute('data-pane') === trTab))
    }
  })
}

// ==== create-run dialog =======================================================
// A modal over the tab (appended to <body> so a background poll re-render of the
// tab can't wipe it). Transient state lives in `form`, not the URL.

interface VariantRow { agent: string; model: string; label: string; kwargs: string }
type ContextMode = 'inline' | 'native' | 'none'
type Effort = 'default' | 'low' | 'medium' | 'high'
interface FormState {
  label: string
  suiteId: string
  suiteName: string
  suiteTasks: number
  variants: VariantRow[]
  contextMode: ContextMode
  effort: Effort
  maxParallel: number
  submitting: boolean
  error: string
}
let catalogCache: { tasks: any[]; agents: any[] } | null = null
let form: FormState | null = null

const OSS_VARIANTS: VariantRow[] = [
  { agent: 'opencode', model: 'openrouter/z-ai/glm-5.3', label: '', kwargs: '' },
  { agent: 'opencode', model: 'openrouter/moonshotai/kimi-k3', label: '', kwargs: '' },
  { agent: 'opencode', model: 'openrouter/qwen/qwen3.8-max-0902', label: '', kwargs: '' },
]
const OPENAI_VARIANTS: VariantRow[] = [
  { agent: 'opencode', model: 'openai/gpt-5.6-sol', label: '', kwargs: '' },
  { agent: 'opencode', model: 'openai/gpt-5.6-terra', label: '', kwargs: '' },
  { agent: 'opencode', model: 'openai/gpt-5.6-luna', label: '', kwargs: '' },
]
const ANTHROPIC_VARIANTS: VariantRow[] = [
  { agent: 'opencode', model: 'tuneloop/bedrock-claude-sonnet-5[1m]', label: '', kwargs: '' },
  { agent: 'opencode', model: 'tuneloop/bedrock-claude-opus-5[1m]', label: '', kwargs: '' },
  { agent: 'opencode', model: 'tuneloop/bedrock-claude-haiku-4-5', label: '', kwargs: '' },
]
const PRESETS: { name: string; variants: VariantRow[] }[] = [
  { name: 'All providers ×9', variants: [...OPENAI_VARIANTS, ...ANTHROPIC_VARIANTS, ...OSS_VARIANTS] },
  { name: 'OpenAI ×3', variants: OPENAI_VARIANTS },
  { name: 'Anthropic ×3', variants: ANTHROPIC_VARIANTS },
  { name: 'Open-source ×3', variants: OSS_VARIANTS },
]

function openDialog(prefill: { suiteId: string; suiteName?: string; suiteTasks?: number; label?: string; variants?: VariantRow[]; contextMode?: ContextMode; effort?: Effort; maxParallel?: number }): void {
  form = {
    label: prefill.label || '',
    suiteId: prefill.suiteId,
    suiteName: prefill.suiteName || 'this suite',
    suiteTasks: prefill.suiteTasks || 0,
    variants: prefill.variants && prefill.variants.length ? prefill.variants.map((v) => ({ ...v })) : [{ ...PRESETS[0]!.variants[0]! }],
    contextMode: prefill.contextMode || 'inline',
    effort: prefill.effort || 'default',
    maxParallel: prefill.maxParallel || 16,
    submitting: false,
    error: '',
  }
  if (catalogCache) { renderDialog(); return }
  get('/api/task-replay/catalog').then((d: any) => { catalogCache = d; renderDialog() })
    .catch(() => { if (form) { form.error = 'Could not load the agent catalog.'; renderDialog() } })
}
function closeDialog(): void { form = null; const el = document.getElementById('tr-modal'); if (el) el.remove() }
function anySeedable(): boolean {
  if (!form || !catalogCache) return false
  const resume = new Set(catalogCache.agents.filter((a: any) => a.resume).map((a: any) => a.id))
  return form.variants.some((v) => resume.has(v.agent))
}
function ctxNote(mode: ContextMode, seedable: boolean): string {
  if (mode === 'inline') return 'every agent gets a distilled recap of the prior turns in its brief — uniform across agents (recommended)'
  if (mode === 'native') return seedable ? 'claude-code/codex resume on the recorded prefix natively; any non-resume agent runs unseeded' : 'none of the selected variants support native seed — they would all run unseeded'
  return 'no prior context — agents get only the task brief'
}
function optionList(current: string, opts: [value: string, label: string][]): string {
  return opts.map(([value, label]) => '<option value="' + value + '"' + (value === current ? ' selected' : '') + '>' + label + '</option>').join('')
}
const CTX_OPTS: [string, string][] = [['inline', 'Inline recap — all agents'], ['native', 'Native seed — claude-code/codex only'], ['none', 'None']]
const EFFORT_OPTS: [string, string][] = [['default', 'Model default'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High']]

function renderDialog(): void {
  if (!form) return
  let el = document.getElementById('tr-modal')
  if (!el) { el = document.createElement('div'); el.id = 'tr-modal'; el.className = 'tr-modal-backdrop'; document.body.appendChild(el) }
  const cat = catalogCache || { tasks: [], agents: [] }
  const cells = form.suiteTasks * form.variants.filter((v) => v.agent && v.model).length
  const overCap = cells > MAX_CELLS
  const seedable = anySeedable()
  const agentOpts = (cur: string) => cat.agents.map((a: any) => '<option value="' + esc(a.id) + '"' + (a.id === cur ? ' selected' : '') + '>' + esc(a.id) + (a.verified ? '' : ' (experimental)') + (a.resume ? '' : ' · no-seed') + '</option>').join('')
  const variantRows = form.variants.map((v, i) =>
    '<div class="tr-vrow" data-vi="' + i + '">' +
    '<select class="tr-v-agent">' + agentOpts(v.agent) + '</select>' +
    '<input class="tr-v-model" placeholder="model id (e.g. us.anthropic.claude-sonnet-5)" value="' + esc(v.model) + '"/>' +
    '<input class="tr-v-label" placeholder="label (optional)" value="' + esc(v.label) + '"/>' +
    '<input class="tr-v-kwargs" placeholder="k=v,k=v (agent flags)" value="' + esc(v.kwargs) + '"/>' +
    '<button type="button" class="tr-v-del" title="remove">–</button></div>').join('')
  el.innerHTML =
    '<div class="tr-modal">' +
    '<div class="tr-modal-head"><h2>New run · ' + esc(form.suiteName) + '</h2><button type="button" class="tr-modal-x" id="trDlgX">✕</button></div>' +
    (form.error ? '<div class="notice tr-dlg-err">' + esc(form.error) + '</div>' : '') +
    '<div class="tr-suite-note">Runs cover all <b>' + num(form.suiteTasks) + '</b> tasks in the suite (the task set is frozen).</div>' +
    '<label class="tr-fld"><span>Label</span><input id="trDlgLabel" value="' + esc(form.label) + '" placeholder="sweep-' + new Date().toISOString().slice(0, 10) + '"/></label>' +
    '<div class="tr-fld"><span>Variants (agent × model)</span>' +
    '<div class="tr-presets">presets: ' + PRESETS.map((p, i) => '<button type="button" class="tr-preset" data-preset="' + i + '">' + esc(p.name) + '</button>').join('') + '</div>' +
    '<div class="tr-vrows">' + variantRows + '</div>' +
    '<button type="button" class="tr-v-add" id="trDlgAddV">+ add variant</button></div>' +
    '<div class="tr-fld tr-adv">' +
    '<label class="tr-ctx">Prior context&nbsp;<select id="trDlgCtx">' + optionList(form.contextMode, CTX_OPTS) + '</select></label>' +
    '<span class="tr-adv-note">' + ctxNote(form.contextMode, seedable) + '</span>' +
    '<label class="tr-ctx">Effort&nbsp;<select id="trDlgEffort">' + optionList(form.effort, EFFORT_OPTS) + '</select></label>' +
    '<span class="tr-adv-note">' + (form.effort === 'default'
      ? 'Each agent runs at its own default effort — not comparable (codex defaults high, claude-code adaptive).'
      : 'Pins reasoning effort to ' + esc(form.effort) + ' across claude-code, codex &amp; opencode so cells are comparable.') + '</span>' +
    '<label class="tr-mp">max parallel <input type="number" id="trDlgMP" min="1" max="32" value="' + form.maxParallel + '"/></label></div>' +
    '<div class="tr-modal-foot"><span class="tr-estimate' + (overCap ? ' tr-over' : '') + '">' +
    num(form.suiteTasks) + ' tasks × ' + form.variants.filter((v) => v.agent && v.model).length + ' variants = <strong>' + cells + '</strong> Modal jobs' +
    (overCap ? ' — over the ' + MAX_CELLS + '-cell cap' : '') + '</span>' +
    '<button type="button" class="btn tr-cancel" id="trDlgCancel">Cancel</button>' +
    '<button type="button" class="btn tr-launch" id="trDlgLaunch"' + (form.submitting || overCap || !cells ? ' disabled' : '') + '>' + (form.submitting ? 'Launching…' : 'Launch') + '</button></div>' +
    '</div>'
  wireDialog(el)
}
function readForm(el: HTMLElement): void {
  if (!form) return
  const lbl = el.querySelector('#trDlgLabel') as HTMLInputElement | null
  if (lbl) form.label = lbl.value
  const mp = el.querySelector('#trDlgMP') as HTMLInputElement | null
  if (mp) form.maxParallel = Math.max(1, Math.min(32, Number(mp.value) || 16))
  const ctx = el.querySelector('#trDlgCtx') as HTMLSelectElement | null
  if (ctx) form.contextMode = ctx.value as ContextMode
  const eff = el.querySelector('#trDlgEffort') as HTMLSelectElement | null
  if (eff) form.effort = eff.value as Effort
  Array.prototype.forEach.call(el.querySelectorAll('.tr-vrow'), (row: HTMLElement) => {
    const i = Number(row.getAttribute('data-vi')); const v = form!.variants[i]; if (!v) return
    v.agent = (row.querySelector('.tr-v-agent') as HTMLSelectElement).value
    v.model = (row.querySelector('.tr-v-model') as HTMLInputElement).value.trim()
    v.label = (row.querySelector('.tr-v-label') as HTMLInputElement).value.trim()
    v.kwargs = (row.querySelector('.tr-v-kwargs') as HTMLInputElement).value.trim()
  })
}
function wireDialog(el: HTMLElement): void {
  const close = () => closeDialog()
  ;(el.querySelector('#trDlgX') as HTMLElement).onclick = close
  ;(el.querySelector('#trDlgCancel') as HTMLElement).onclick = close
  el.onclick = (e) => { if (e.target === el) close() }
  ;(el.querySelector('#trDlgAddV') as HTMLElement).onclick = () => { readForm(el); form!.variants.push({ agent: 'claude-code', model: '', label: '', kwargs: '' }); renderDialog() }
  Array.prototype.forEach.call(el.querySelectorAll('.tr-v-del'), (b: HTMLElement) => {
    b.onclick = () => { readForm(el); const i = Number((b.closest('.tr-vrow') as HTMLElement).getAttribute('data-vi')); form!.variants.splice(i, 1); if (!form!.variants.length) form!.variants.push({ agent: 'claude-code', model: '', label: '', kwargs: '' }); renderDialog() }
  })
  Array.prototype.forEach.call(el.querySelectorAll('.tr-preset'), (b: HTMLElement) => {
    b.onclick = () => { readForm(el); const p = PRESETS[Number(b.getAttribute('data-preset'))]!; form!.variants = p.variants.map((v) => ({ ...v })); renderDialog() }
  })
  Array.prototype.forEach.call(el.querySelectorAll('.tr-v-agent'), (s: HTMLElement) => { s.onchange = () => { readForm(el); renderDialog() } })
  const ctx = el.querySelector('#trDlgCtx') as HTMLElement | null
  if (ctx) ctx.onchange = () => { readForm(el); renderDialog() }
  const eff = el.querySelector('#trDlgEffort') as HTMLElement | null
  if (eff) eff.onchange = () => { readForm(el); renderDialog() }
  ;(el.querySelector('#trDlgLaunch') as HTMLElement).onclick = () => submitDialog(el)
}
function submitDialog(el: HTMLElement): void {
  if (!form) return
  readForm(el)
  const variants = form.variants.filter((v) => v.agent && v.model).map((v) => ({ agent: v.agent, model: v.model, label: v.label || undefined, kwargs: parseKwargs(v.kwargs) }))
  form.submitting = true; form.error = ''; renderDialog()
  postStatus('/api/task-replay/runs', { label: form.label, suiteId: form.suiteId, variants, contextMode: form.contextMode, effort: form.effort, maxParallel: form.maxParallel })
    .then((r: any) => {
      if (r.status >= 200 && r.status < 300 && r.body && r.body.runId) { closeDialog(); ovCache = null; goRun(r.body.runId) }
      else if (form) { form.submitting = false; form.error = (r.body && r.body.error) || 'Launch failed.'; renderDialog() }
    }).catch(() => { if (form) { form.submitting = false; form.error = 'Launch failed — is the server reachable?'; renderDialog() } })
}
function parseKwargs(s: string): Record<string, string> {
  const out: Record<string, string> = {}
  ;(s || '').split(',').forEach((pair) => { const eq = pair.indexOf('='); if (eq < 0) return; const k = pair.slice(0, eq).trim(); const v = pair.slice(eq + 1).trim(); if (k) out[k] = v })
  return out
}
function rerunPrefill(run: any): Parameters<typeof openDialog>[0] {
  const p = (run && run.params) || {}
  const variants: VariantRow[] = (p.variants || []).map((v: any) => ({ agent: v.agent || 'claude-code', model: v.model || '', label: v.label || '', kwargs: Object.keys(v.kwargs || {}).map((k) => k + '=' + v.kwargs[k]).join(',') }))
  return { suiteId: run.suiteId || state.tr.suite, suiteName: run.suiteName || 'this suite', suiteTasks: run.tasks || 0, label: '', variants, contextMode: (p.contextMode || 'inline') as ContextMode, effort: (p.effort || 'default') as Effort, maxParallel: p.maxParallel || 16 }
}

// ==== suite builder (Tier 0) ==================================================
// Freeze a new suite by picking tasks from the pool: a filterable table
// (multi-select task-type / complexity / repo / owner, plus search) with a live
// selection tally. Membership
// is frozen on create. Transient state lives in `suiteForm`; the whole modal
// re-renders only on open/error/submit — filter + selection changes repaint
// just the rows/tally (via refreshSuiteTable) so input focus is never lost.

interface SuiteForm {
  name: string
  tasks: Set<string>
  // Multi-select facets, [] = all. A task missing the field matches SB_NONE.
  typeF: string[]
  cxF: string[]
  repoF: string[]
  ownerF: string[]
  q: string // free-text search over task title/id
  submitting: boolean
  error: string
}
let suiteForm: SuiteForm | null = null

function openSuiteBuilder(): void {
  suiteForm = { name: '', tasks: new Set(), typeF: [], cxF: [], repoF: [], ownerF: [], q: '', submitting: false, error: '' }
  if (catalogCache) { renderSuiteBuilder(); return }
  get('/api/task-replay/catalog').then((d: any) => { catalogCache = d; renderSuiteBuilder() })
    .catch(() => { if (suiteForm) { suiteForm.error = 'Could not load the task catalog.'; renderSuiteBuilder() } })
}
function closeSuiteBuilder(): void { suiteForm = null; const el = document.getElementById('tr-suite-modal'); if (el) el.remove() }

// The facet value a task filters on — SB_NONE when the field is unset, so tasks
// without an owner/complexity/… stay reachable through a "(none)" option.
type SbFacet = 'taskType' | 'complexity' | 'repo' | 'owner'
const SB_NONE = '__none__'
function sbVal(t: any, field: SbFacet): string { return t[field] ? String(t[field]) : SB_NONE }

// The pool tasks matching the current facet + search filters.
function sbFiltered(): any[] {
  const cat = catalogCache || { tasks: [] }
  const f = suiteForm!
  const q = f.q.trim().toLowerCase()
  const pass = (sel: string[], t: any, field: SbFacet) => !sel.length || sel.indexOf(sbVal(t, field)) >= 0
  return cat.tasks.filter((t: any) => {
    if (!pass(f.typeF, t, 'taskType') || !pass(f.cxF, t, 'complexity')) return false
    if (!pass(f.repoF, t, 'repo') || !pass(f.ownerF, t, 'owner')) return false
    if (q && (t.title || t.id || '').toLowerCase().indexOf(q) < 0) return false
    return true
  })
}
// A facet's options: the values present in the pool, in `order` (default A→Z),
// with "(none)" last when some task lacks the field.
function sbOptions(field: SbFacet, order?: (vals: string[]) => string[]): MsOpt[] {
  const cat = catalogCache || { tasks: [] }
  const vals = [...new Set(cat.tasks.map((t: any) => sbVal(t, field)))] as string[]
  const known = vals.filter((v) => v !== SB_NONE)
  const opts = (order ? order(known) : known.sort()).map((v) => ({ v, label: v }))
  return vals.indexOf(SB_NONE) >= 0 ? [...opts, { v: SB_NONE, label: '(none)' }] : opts
}
function sortComplexity(vals: string[]): string[] {
  const rank = (v: string) => { const i = SB_COMPLEXITY.indexOf(v); return i < 0 ? SB_COMPLEXITY.length : i }
  return vals.slice().sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}
function sbTypeCell(ty: string | null): string {
  if (!ty) return '<span class="tr-ttype">—</span>'
  return '<span class="tr-ttype"><span class="tr-ttype-dot" style="background:' + typeColor(ty) + '"></span>' + esc(ty) + '</span>'
}
function sbTurns(t: any): string {
  if (t.turnStart == null) return '—'
  return t.turnStart + '–' + (t.turnEnd == null ? t.turnStart : t.turnEnd)
}
function sbGt(t: any): string {
  const k = (t.gtKind || '').toLowerCase()
  return k ? '<span class="tr-gt">' + esc(k) + '</span>' : ''
}
// The classify-turns complexity label, graded light → dark (unknown labels render plain).
const SB_COMPLEXITY = ['trivial', 'routine', 'substantial', 'open-ended']
function sbComplexity(t: any): string {
  const c = String(t.complexity || '')
  if (!c) return '<span class="tr-sb-cx">—</span>'
  const lvl = SB_COMPLEXITY.indexOf(c)
  return '<span class="tr-sb-cx' + (lvl >= 0 ? ' tr-sb-cx-' + lvl : '') + '">' + esc(c) + '</span>'
}
function sbRowsHtml(): string {
  const rows = sbFiltered()
  if (!rows.length) return '<tr><td colspan="7" class="tr-sb-empty">No tasks match these filters.</td></tr>'
  return rows.map((t: any) =>
    '<tr class="tr-sb-row" data-task="' + esc(t.id) + '">' +
    '<td class="tr-sb-c"><input type="checkbox" class="tr-sb-cb" data-task="' + esc(t.id) + '"' + (suiteForm!.tasks.has(t.id) ? ' checked' : '') + '/></td>' +
    '<td class="tr-sb-title">' + esc(t.title || t.id) + '</td>' +
    '<td>' + sbTypeCell(t.taskType) + '</td>' +
    '<td>' + sbComplexity(t) + '</td>' +
    '<td class="tr-sb-repo">' + esc(t.repo || '—') + '</td>' +
    '<td class="tr-sb-turns">' + sbTurns(t) + '</td>' +
    '<td>' + sbGt(t) + '</td></tr>').join('')
}
function sbTallyHtml(): string {
  const cat = catalogCache || { tasks: [] }
  const sel = suiteForm!.tasks
  const byTy: Record<string, number> = {}
  cat.tasks.forEach((t: any) => { if (sel.has(t.id) && t.taskType) byTy[t.taskType] = (byTy[t.taskType] || 0) + 1 })
  const chips = TYPES.filter((t) => byTy[t.key]).map((t) =>
    '<span class="tr-sb-tychip"><span class="tr-mix-sw" style="background:' + typeColor(t.key) + '"></span>' + byTy[t.key] + ' ' + esc(t.label.toLowerCase()) + '</span>').join('')
  return '<span><b>' + sel.size + '</b> of <b>' + cat.tasks.length + '</b> tasks selected</span>' +
    '<span class="tr-sb-tychips">' + chips + '</span>'
}
// The header checkbox mirrors the visible rows: checked when all are selected,
// indeterminate when some are.
function syncSbAll(el: HTMLElement): void {
  const all = el.querySelector('#trSbAll') as HTMLInputElement | null
  if (!all) return
  const vis = sbFiltered()
  const on = vis.filter((t: any) => suiteForm!.tasks.has(t.id)).length
  all.checked = vis.length > 0 && on === vis.length
  all.indeterminate = on > 0 && on < vis.length
}

function renderSuiteBuilder(): void {
  if (!suiteForm) return
  let el = document.getElementById('tr-suite-modal')
  if (!el) { el = document.createElement('div'); el.id = 'tr-suite-modal'; el.className = 'tr-modal-backdrop'; document.body.appendChild(el) }
  const f = suiteForm
  // dropUnknown: a client-side facet — a value gone from the pool matches nothing.
  const ms = (id: string, label: string, all: string, noun: string, opts: MsOpt[], sel: string[]) =>
    msSelect(id, label, all, noun, opts, sel, { dropUnknown: true })
  el.innerHTML =
    '<div class="tr-modal tr-sb-modal">' +
    '<div class="tr-modal-head tr-sb-head"><div><h2>New suite</h2>' +
    '<p class="tr-sb-sub">Select tasks from the pool. The suite is <b>frozen</b> on create — to change it later, create a new suite.</p></div>' +
    '<button type="button" class="tr-modal-x" id="trSbX">✕</button></div>' +
    (f.error ? '<div class="notice tr-dlg-err tr-sb-err">' + esc(f.error) + '</div>' : '') +
    '<div class="tr-sb-body">' +
    '<label class="tr-fld"><span>Suite name</span><input id="trSbName" type="text" value="' + esc(f.name) + '" placeholder="e.g. Core routing v1"/></label>' +
    '<div class="tr-sb-filters">' +
    ms('trSbType', 'Task type', 'All task types', 'types', sbOptions('taskType', sortTypes), f.typeF) +
    ms('trSbCx', 'Complexity', 'All complexities', 'levels', sbOptions('complexity', sortComplexity), f.cxF) +
    ms('trSbRepo', 'Repo', 'All repos', 'repos', sbOptions('repo'), f.repoF) +
    ms('trSbOwner', 'Owner', 'All owners', 'owners', sbOptions('owner'), f.ownerF) +
    '<span class="tr-sb-search"><input type="text" id="trSbQ" placeholder="Search tasks…" value="' + esc(f.q) + '"/></span>' +
    '</div>' +
    '<div class="tr-sb-tally" id="trSbTally">' + sbTallyHtml() + '</div>' +
    '<div class="tr-sb-scroll"><table class="tr-sb-tbl">' +
    '<thead><tr><th class="tr-sb-c"><input type="checkbox" id="trSbAll"/></th><th>Task</th><th>Type</th><th>Complexity</th><th>Repo</th><th>Turns</th><th>GT</th></tr></thead>' +
    '<tbody id="trSbRows">' + sbRowsHtml() + '</tbody></table></div>' +
    '</div>' +
    '<div class="tr-modal-foot tr-sb-foot">' +
    '<div class="tr-sb-note">Freezing captures these task IDs as a snapshot. Runs inside the suite always cover exactly this set.</div>' +
    '<div class="tr-sb-acts"><button type="button" class="btn tr-cancel" id="trSbCancel">Cancel</button>' +
    '<button type="button" class="btn tr-launch" id="trSbCreate"' + (f.submitting || !f.tasks.size || !f.name.trim() ? ' disabled' : '') +
    ' title="' + esc(!f.name.trim() ? 'Enter a suite name to continue' : !f.tasks.size ? 'Select at least one task' : 'Freeze this task set as a new suite') + '">' +
    (f.submitting ? 'Creating…' : 'Freeze &amp; create suite') + '</button></div>' +
    '</div></div>'
  syncSbAll(el)
  wireSuiteBuilder(el)
}
// Repaint just the rows + tally (+ select-all state, create button) after a
// filter/selection change, leaving the name/filter inputs and their focus.
function refreshSuiteTable(el: HTMLElement): void {
  const rows = el.querySelector('#trSbRows'); if (rows) rows.innerHTML = sbRowsHtml()
  const tally = el.querySelector('#trSbTally'); if (tally) tally.innerHTML = sbTallyHtml()
  syncSbAll(el)
  const b = el.querySelector('#trSbCreate') as HTMLButtonElement | null
  if (b) updateSbCreate(b)
  wireSbRows(el)
}
// The create button is enabled only with a name + at least one task; its
// tooltip explains whichever requirement is still unmet. One place, three
// callers (render, refresh, name input).
function updateSbCreate(b: HTMLButtonElement): void {
  const f = suiteForm!
  b.disabled = f.submitting || !f.tasks.size || !f.name.trim()
  b.title = !f.name.trim() ? 'Enter a suite name to continue'
    : !f.tasks.size ? 'Select at least one task'
    : 'Freeze this task set as a new suite'
}
function toggleSbTask(id: string, el: HTMLElement): void {
  suiteForm!.tasks.has(id) ? suiteForm!.tasks.delete(id) : suiteForm!.tasks.add(id)
  refreshSuiteTable(el)
}
function wireSbRows(el: HTMLElement): void {
  Array.prototype.forEach.call(el.querySelectorAll('.tr-sb-cb'), (cb: HTMLInputElement) => {
    cb.onchange = () => toggleSbTask(cb.getAttribute('data-task')!, el)
  })
  Array.prototype.forEach.call(el.querySelectorAll('.tr-sb-row'), (r: HTMLElement) => {
    r.onclick = (e) => { if ((e.target as HTMLElement).closest('input')) return; toggleSbTask(r.getAttribute('data-task')!, el) }
  })
}
function wireSuiteBuilder(el: HTMLElement): void {
  const close = () => closeSuiteBuilder()
  ;(el.querySelector('#trSbX') as HTMLElement).onclick = close
  ;(el.querySelector('#trSbCancel') as HTMLElement).onclick = close
  el.onclick = (e) => { if (e.target === el) close() }
  const name = el.querySelector('#trSbName') as HTMLInputElement | null
  if (name) name.oninput = () => { suiteForm!.name = name.value; const b = el.querySelector('#trSbCreate') as HTMLButtonElement | null; if (b) updateSbCreate(b) }
  // Live: the table re-filters on every tick (client-side, no fetch).
  const facet = (id: string, key: 'typeF' | 'cxF' | 'repoF' | 'ownerF') =>
    wireMs(el, id, (vals) => { if (!suiteForm) return; suiteForm[key] = vals; refreshSuiteTable(el) }, { live: true })
  facet('trSbType', 'typeF')
  facet('trSbCx', 'cxF')
  facet('trSbRepo', 'repoF')
  facet('trSbOwner', 'ownerF')
  const q = el.querySelector('#trSbQ') as HTMLInputElement | null
  if (q) q.oninput = () => { suiteForm!.q = q.value; refreshSuiteTable(el) }
  const all = el.querySelector('#trSbAll') as HTMLInputElement | null
  if (all) all.onchange = () => {
    const vis = sbFiltered()
    vis.forEach((t: any) => { all.checked ? suiteForm!.tasks.add(t.id) : suiteForm!.tasks.delete(t.id) })
    refreshSuiteTable(el)
  }
  ;(el.querySelector('#trSbCreate') as HTMLElement).onclick = () => submitSuite(el)
  wireSbRows(el)
}
function submitSuite(el: HTMLElement): void {
  if (!suiteForm) return
  const nameEl = el.querySelector('#trSbName') as HTMLInputElement | null
  if (nameEl) suiteForm.name = nameEl.value
  const name = suiteForm.name.trim()
  if (!name || !suiteForm.tasks.size) return
  suiteForm.submitting = true; suiteForm.error = ''; renderSuiteBuilder()
  postStatus('/api/task-replay/suites', { name, tasks: [...suiteForm.tasks] })
    .then((r: any) => {
      if (r.status >= 200 && r.status < 300 && r.body && r.body.suiteId) { closeSuiteBuilder(); suitesCache = null; goSuite(r.body.suiteId) }
      else if (suiteForm) { suiteForm.submitting = false; suiteForm.error = (r.body && r.body.error) || 'Create failed.'; renderSuiteBuilder() }
    }).catch(() => { if (suiteForm) { suiteForm.submitting = false; suiteForm.error = 'Create failed — is the server reachable?'; renderSuiteBuilder() } })
}

// ==== shared bits =============================================================

function suitesBack(): string { return '<a class="bench-back" href="javascript:void(0)" data-back="suites" title="Back to all suites">&larr;</a>' }
function overviewBack(): string { return '<a class="bench-back" href="javascript:void(0)" data-back="overview" title="Back to the suite">&larr;</a>' }
function runBack(runId: string): string { return '<a class="bench-back" href="javascript:void(0)" data-back="run:' + esc(runId) + '" title="Back to the run">&larr;</a>' }
function wireBack(root: HTMLElement): void {
  const back = root.querySelector('[data-back]') as HTMLElement | null
  if (!back) return
  const target = back.getAttribute('data-back') || 'overview'
  back.onclick = () => {
    if (target.indexOf('run:') === 0) goRun(target.slice(4))
    else if (target === 'suites') goSuites()
    else goOverview()
  }
}

/** GitHub-style diff: one card per file, colored line backgrounds. */
export function ghDiff(diff: string): string {
  let out = '', body = '', file = ''
  const flush = () => { if (file || body) out += '<div class="ghd-file"><div class="ghd-head mono">' + esc(file || '(unknown file)') + '</div><pre class="ghd-body mono">' + body + '</pre></div>'; body = '' }
  diff.split('\n').forEach((line) => {
    if (line.indexOf('diff --git') === 0) { flush(); const mm = / b\/(.+)$/.exec(line); file = mm ? mm[1]! : ''; return }
    if (line.indexOf('+++') === 0 || line.indexOf('---') === 0 || line.indexOf('index ') === 0) return
    const cls = line.indexOf('@@') === 0 ? 'ghd-hunk' : line.indexOf('+') === 0 ? 'ghd-add' : line.indexOf('-') === 0 ? 'ghd-del' : 'ghd-ctx'
    body += '<span class="' + cls + '">' + esc(line || ' ') + '\n</span>'
  })
  flush()
  return '<div class="exp-diff-files">' + (out || '<pre class="exp-diffpre">' + esc(diff) + '</pre>') + '</div>'
}
